import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import TripSheetModal from './TripSheetModal';
import { formatCurrency, getCurrencySymbol } from '../../utils/formatters';

const API = '/api';

const Icon = ({ d, size = 16, strokeWidth = 1.75 }) => (
    <svg
        width={size} height={size} viewBox="0 0 24 24"
        fill="none" stroke="currentColor"
        strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
    >
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const icons = {
    search: ['M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z', 'M21 21l-4.35-4.35'],
    plus: ['M12 5v14', 'M5 12h14'],
    edit: ['M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7', 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z'],
    trash: ['M3 6h18', 'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2'],
    x: ['M18 6 6 18', 'M6 6l12 12'],
    map: ['M1 6v16l7-4 8 4 7-4V2l-7 4-8-4-7 4z', 'M8 2v16', 'M16 6v16'],
    upload: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M17 8l-5-5-5 5 M12 3v12',
    download: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M7 10l5 5 5-5 M12 15V3',
    truck: ['M1 3h15v13H1z', 'M16 8h4l3 3v5h-7V8z', 'M5.5 21a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z', 'M18.5 21a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z'],
    user: ['M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2', 'M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z'],
    check: ['M20 6L9 17l-5-5'],
    calendar: ['M8 2v4', 'M16 2v4', 'M3 10h18', 'M21 8a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V8z'],
    car: ['M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.5 2.5C1.4 11.3 1 12.1 1 13v3c0 .6.4 1 1 1h2', 'M7 17a2 2 0 1 0 4 0 2 2 0 0 0-4 0', 'M15 17a2 2 0 1 0 4 0 2 2 0 0 0-4 0'],
    clock: ['M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z', 'M12 6v6l4 2'],
};

/* ── Availability Panel ── */
function AvailabilityPanel({ onClose, vehicles, drivers, vendorFleet, trips }) {
    const [tab, setTab] = useState('fleet'); // fleet | drivers | vfleet | vdrivers
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const HOURS = Array.from({ length: 24 }, (_, i) => i);

    const COLORS = {
        Available: { bg: 'linear-gradient(135deg,#4ade80,#22c55e)', border: '#86efac', shadow: 'rgba(34,197,94,.2)', text: '#15803d' },
        Daily: { bg: 'linear-gradient(135deg,#fb923c,#f97316)', border: '#fdba74', shadow: 'rgba(249,115,22,.25)', text: '#ea580c' },
        Monthly: { bg: 'linear-gradient(135deg,#a78bfa,#7c3aed)', border: '#c4b5fd', shadow: 'rgba(124,58,237,.25)', text: '#7c3aed' },
        Shuttle: { bg: 'linear-gradient(135deg,#60a5fa,#3b82f6)', border: '#93c5fd', shadow: 'rgba(59,130,246,.25)', text: '#1d4ed8' },
        'Shuttle with Booking': { bg: 'linear-gradient(135deg,#22d3ee,#06b6d4)', border: '#67e8f9', shadow: 'rgba(6,182,212,.25)', text: '#0891b2' },
        'Ad-hoc': { bg: 'linear-gradient(135deg,#fcd34d,#f59e0b)', border: '#fde68a', shadow: 'rgba(245,158,11,.25)', text: '#b45309' },
        Absent: { bg: 'linear-gradient(135deg,#f87171,#ef4444)', border: '#fca5a5', shadow: 'rgba(239,68,68,.25)', text: '#b91c1c' },
    };

    const getLeaveAtDate = (entity) => {
        if (!entity || !entity.leaves) return null;
        const target = new Date(); // In the modal we usually look at 'Today'
        return entity.leaves.find(l => {
            if (l.status !== 'Approved' && l.status !== 'Active') return false;
            const start = new Date(l.startDate);
            const end = new Date(l.endDate);
            return target >= start && target <= end;
        });
    };

    const getTripAtHour = (entityId, hour) => {
        const activeTrips = trips.filter(t => {
            const vid = t.vehicleId?._id || t.vehicleId;
            const did = t.driverId?._id || t.driverId;
            const vfid = t.vendorProposedVehicleId?._id || t.vendorProposedVehicleId;
            return (vid === entityId || did === entityId || vfid === entityId) &&
                ['Scheduled', 'Ongoing'].includes(t.status);
        });
        return activeTrips.find(t => {
            if (t.billingType === 'Monthly') return true;
            if (!t.startTime) return false;
            const [sh] = t.startTime.split(':').map(Number);
            const [eh] = (t.endTime || t.startTime).split(':').map(Number);
            return hour >= sh && hour <= (eh || sh);
        });
    };

    const generalDrivers = drivers.filter(d => !d.vendorId);
    const vendorDrivers = drivers.filter(d => d.vendorId);

    const TABS = [
        { key: 'fleet', label: 'General Fleet', count: vehicles.length, icon: icons.car },
        { key: 'drivers', label: 'General Drivers', count: generalDrivers.length, icon: icons.user },
        { key: 'vfleet', label: 'Vendor Fleet', count: vendorFleet.length, icon: icons.truck },
        { key: 'vdrivers', label: 'Vendor Drivers', count: vendorDrivers.length, icon: icons.user },
    ];

    const allRows = (() => {
        const q = search.toLowerCase();
        if (tab === 'fleet') return vehicles.filter(v => (v.registrationNumber + (v.make || '')).toLowerCase().includes(q)).map(v => ({ id: v._id, name: v.registrationNumber, sub: v.make + ' ' + (v.model || ''), icon: icons.car, raw: v }));
        if (tab === 'drivers') return generalDrivers.filter(d => d.name?.toLowerCase().includes(q)).map(d => ({ id: d._id, name: d.name, sub: d.phoneNumber || '', icon: icons.user, raw: d }));
        if (tab === 'vfleet') return vendorFleet.filter(f => (f.registrationNumber || '').toLowerCase().includes(q)).map(f => ({ id: f._id, name: f.registrationNumber, sub: f.vehicleType || f.make || '', icon: icons.truck, raw: f }));
        if (tab === 'vdrivers') return vendorDrivers.filter(d => d.name?.toLowerCase().includes(q)).map(d => ({ id: d._id, name: d.name, sub: d.phoneNumber || '', icon: icons.user, raw: d }));
        return [];
    })();

    const currentHour = new Date().getHours();

    const rows = allRows.filter(r => {
        if (statusFilter === 'all') return true;
        if (statusFilter === 'Available') return !getTripAtHour(r.id, currentHour) && !getLeaveAtDate(r.raw);
        if (statusFilter === 'Absent') return !!getLeaveAtDate(r.raw);
        if (statusFilter === 'On Trip') return !!getTripAtHour(r.id, currentHour);
        
        // Specific billing type filter
        return HOURS.some(h => {
            const t = getTripAtHour(r.id, h);
            return t && t.billingType === statusFilter;
        });
    });

    const availCount = allRows.filter(r => !getTripAtHour(r.id, currentHour) && !getLeaveAtDate(r.raw)).length;
    const onTripCount = allRows.filter(r => !!getTripAtHour(r.id, currentHour)).length;

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
            <div style={{ background: '#fff', borderRadius: 20, width: '92%', maxWidth: 900, maxHeight: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 25px 60px rgba(0,0,0,0.25)' }} onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div style={{ padding: '18px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#063A5C' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Icon d={icons.calendar} size={18} strokeWidth={2} />
                        <span style={{ color: 'white', fontWeight: 800, fontSize: 16 }}>Availability Overview</span>
                        <span style={{ background: 'rgba(255,255,255,0.15)', color: 'white', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20 }}>Today</span>
                    </div>
                    <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: 'white', borderRadius: 8, width: 30, height: 30, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Icon d={icons.x} size={16} />
                    </button>
                </div>

                {/* Tabs */}
                <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid #f1f5f9', background: '#f8fafc' }}>
                    {TABS.map(t => (
                        <button key={t.key} onClick={() => { setTab(t.key); setSearch(''); }}
                            style={{ flex: 1, padding: '12px 8px', border: 'none', cursor: 'pointer', background: tab === t.key ? '#fff' : 'transparent', borderBottom: tab === t.key ? '2px solid #16539C' : '2px solid transparent', fontWeight: tab === t.key ? 800 : 600, color: tab === t.key ? '#16539C' : '#64748b', fontSize: 13, transition: 'all 0.15s' }}>
                            {t.label}
                            <span style={{ marginLeft: 6, background: tab === t.key ? '#e6f0fa' : '#f1f5f9', color: tab === t.key ? '#16539C' : '#94a3b8', borderRadius: 20, padding: '1px 7px', fontSize: 11, fontWeight: 700 }}>{t.count}</span>
                        </button>
                    ))}
                </div>

                {/* Summary Cards */}
                <div style={{ display: 'flex', gap: 12, padding: '14px 20px 0', background: '#fff' }}>
                    {[
                        { label: 'Total', val: allRows.length, bg: '#eff6ff', color: '#1d4ed8', key: 'all' },
                        { label: 'Available Now', val: availCount, bg: '#f0fdf4', color: '#166534', key: 'Available' },
                        { label: 'On Trips Now', val: onTripCount, bg: 'rgba(249,115,22,.15)', color: '#ea580c', key: 'On Trip' },
                        { label: 'Absent Today', val: allRows.filter(r => getLeaveAtDate(r.raw)).length, bg: 'rgba(239,68,68,.15)', color: '#b91c1c', key: 'Absent' },
                    ].map(s => (
                        <div 
                            key={s.label} 
                            onClick={() => setStatusFilter(s.key)}
                            style={{ 
                                flex: 1, background: s.bg, borderRadius: 12, padding: '10px 16px', textAlign: 'center', cursor: 'pointer',
                                transition: 'all 0.2s', transform: statusFilter === s.key ? 'scale(1.05)' : 'none',
                                boxShadow: statusFilter === s.key ? '0 4px 12px rgba(0,0,0,0.1)' : 'none',
                                border: statusFilter === s.key ? `2px solid ${s.color}` : '2px solid transparent'
                            }}
                        >
                            <div style={{ fontSize: 26, fontWeight: 900, color: s.color }}>{s.val}</div>
                            <div style={{ fontSize: 11, fontWeight: 700, color: s.color, opacity: 0.75 }}>{s.label}</div>
                        </div>
                    ))}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '0 8px', flexWrap: 'wrap', maxWidth: 320, justifyContent: 'flex-end' }}>
                        {Object.entries(COLORS).map(([l, c]) => (
                            <div 
                                key={l} 
                                onClick={() => setStatusFilter(l)}
                                style={{ 
                                    display: 'flex', alignItems: 'center', gap: 5, fontSize: 10, fontWeight: 700, 
                                    color: statusFilter === l ? '#000' : '#374151', cursor: 'pointer',
                                    background: statusFilter === l ? '#f1f5f9' : 'transparent',
                                    padding: '2px 6px', borderRadius: 6
                                }}
                            >
                                <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 3, background: c.bg.split(',')[1].replace(')', '') }} /> 
                                {l.split(' ')[0]}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Search */}
                <div style={{ padding: '10px 20px', background: '#fff' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0', padding: '0 12px' }}>
                        <Icon d={icons.search} size={13} />
                        <input value={search} onChange={e => setSearch(e.target.value)} placeholder={`Search ${TABS.find(t => t.key === tab)?.label}...`}
                            style={{ flex: 1, border: 'none', outline: 'none', padding: '9px 0', fontSize: 13, background: 'transparent', fontFamily: 'inherit' }} />
                    </div>
                </div>

                {/* Grid */}
                <div style={{ flex: 1, overflowY: 'auto', overflowX: 'auto', padding: '0 20px 20px' }}>
                    {rows.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '50px 20px', color: '#94a3b8', fontSize: 13 }}>No records found</div>
                    ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 800 }}>
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 11, fontWeight: 700, color: '#94a3b8', whiteSpace: 'nowrap', minWidth: 160 }}>Name</th>
                                    <th style={{ textAlign: 'left', padding: '8px 8px', fontSize: 11, fontWeight: 700, color: '#94a3b8', whiteSpace: 'nowrap', width: 80 }}>Hours</th>
                                    {HOURS.map(h => (
                                        <th key={h} style={{ padding: '4px 2px', fontSize: 9, fontWeight: 700, color: '#94a3b8', textAlign: 'center', width: 26 }}>
                                            {h === 0 ? '12' : h > 12 ? h - 12 : h}
                                            <div style={{ fontSize: 8, color: '#cbd5e1' }}>{h < 12 ? 'AM' : 'PM'}</div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map(r => {
                                    const leave = getLeaveAtDate(r.raw);
                                    const busyHours = HOURS.map(h => getTripAtHour(r.id, h)).filter(Boolean);
                                    const isAvail = !leave && busyHours.length === 0;
                                    const isMonthly = busyHours.some(t => t.billingType === 'Monthly');
                                    
                                    const statusKey = leave ? 'Absent' : (isMonthly ? 'Monthly' : (isAvail ? 'Available' : 'Daily'));
                                    const sc = COLORS[statusKey];

                                    return (
                                        <tr key={r.id} style={{ borderBottom: '1px solid #f8fafc' }}>
                                            <td style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
                                                <div style={{ 
                                                    width: 30, height: 30, borderRadius: 8, 
                                                    background: sc.bg.split(',')[1].replace(')', '') + '15', 
                                                    color: sc.text, display: 'flex', alignItems: 'center', 
                                                    justifyContent: 'center', flexShrink: 0 
                                                }}>
                                                    <Icon d={r.icon} size={15} color={sc.text} strokeWidth={2.2} />
                                                </div>
                                                <div>
                                                    <div style={{ fontWeight: 700, fontSize: 12, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 4 }}>
                                                        {r.name}
                                                        {isMonthly && <span style={{ fontSize: 8, background: COLORS.Monthly.text, color: 'white', padding: '0 4px', borderRadius: 3 }}>M</span>}
                                                        {leave && <span style={{ fontSize: 8, background: COLORS.Absent.text, color: 'white', padding: '0 4px', borderRadius: 3 }}>ABSENT</span>}
                                                    </div>
                                                    <div style={{ fontSize: 10, color: '#94a3b8' }}>{r.sub}</div>
                                                </div>
                                            </td>
                                            <td style={{ padding: '8px', fontSize: 11, color: sc.text, whiteSpace: 'nowrap', fontWeight: 700 }}>{leave ? (leave.leaveType || 'Leave') : (isMonthly ? 'Dedicated' : (busyHours.length > 0 ? `${busyHours.length}h busy` : '0h busy'))}</td>
                                            {HOURS.map(h => {
                                                const trip = getTripAtHour(r.id, h);
                                                const busy = !!trip;
                                                const type = trip?.billingType || (leave ? 'Absent' : 'Available');
                                                const style = COLORS[type] || COLORS.Daily;
                                                const tooltip = leave ? `Absent: ${leave.leaveType}` : (busy ? `${trip.clientId?.companyName || 'Trip'} (${type})` : 'Available');
                                                return (
                                                    <td key={h} style={{ padding: '3px 2px' }}>
                                                        <div title={tooltip} style={{ width: 22, height: 22, borderRadius: 4, background: style.bg, border: `1.5px solid ${style.border}`, cursor: 'default' }} />
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}

// ---- Trip timing / overdue helpers -------------------------------------------
const localDateStr = (d = new Date()) => {
    const x = new Date(d);
    if (isNaN(x)) return '';
    return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
};
const parseHM = (val) => {
    if (!val) return null;
    const str = String(val).trim();
    if (str.includes('T')) { const d = new Date(str); return isNaN(d) ? null : [d.getHours(), d.getMinutes()]; }
    const m = str.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*([AaPp][Mm])?$/);
    if (!m) return null;
    let h = parseInt(m[1], 10);
    const min = parseInt(m[2], 10);
    if (m[3]) { const pm = /p/i.test(m[3]); if (pm && h < 12) h += 12; if (!pm && h === 12) h = 0; }
    return [h, min];
};
const atTime = (dateStr, val) => {
    const hm = parseHM(val);
    if (!dateStr || !hm) return null;
    const d = new Date(dateStr + 'T00:00:00');
    d.setHours(hm[0], hm[1], 0, 0);
    return d;
};
const PRE_SCHEDULE_STATUSES = ['Requested', 'PriceProposed', 'ClientAccepted', 'Rejected'];
const tripRunsOnDate = (t, dateStr) => {
    if (!dateStr) return false;
    if (t.tripFrequency === 'Recurring') {
        const wd = new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short' });
        if (!t.recurringDays?.includes(wd)) return false;
        if (t.startDate && localDateStr(t.startDate) > dateStr) return false;
        if (t.endDate && localDateStr(t.endDate) < dateStr) return false;
        return true;
    }
    return !!t.tripDate && localDateStr(t.tripDate) === dateStr;
};
const fmtLate = (mins) => {
    if (mins < 60) return `${mins}m`;
    if (mins < 1440) return `${Math.floor(mins / 60)}h ${mins % 60}m`;
    return `${Math.floor(mins / 1440)}d ${Math.floor((mins % 1440) / 60)}h`;
};
// Same thresholds as backend tripAlerts.js
const TIMING_META = {
    delayed:     { label: 'Delayed',          color: '#b45309', bg: '#fef3c7' },
    overdue:     { label: 'Overdue',          color: '#c2410c', bg: '#ffedd5' },
    critical:    { label: 'Critical',         color: '#b91c1c', bg: '#fee2e2' },
    missed:      { label: 'Missed',           color: '#7f1d1d', bg: '#fecaca' },
    overrunning: { label: 'Overrunning',      color: '#6d28d9', bg: '#ede9fe' },
};
const NOT_STARTED_STATES = ['delayed', 'overdue', 'critical', 'missed'];

export default function Trips({ user, onNavigate, operatorDetails }) {
    const [trips, setTrips] = useState([]);
    const [instances, setInstances] = useState([]);
    const [routes, setRoutes] = useState([]);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const todayStr = new Date().toISOString().split('T')[0];
    const [filters, setFilters] = useState({ date: todayStr, client: '', route: '', billingType: '', safetyOnly: false, overdueOnly: false });
    const [now, setNow] = useState(() => new Date());
    const [showAnalytics, setShowAnalytics] = useState(true);
    useEffect(() => { const id = setInterval(() => setNow(new Date()), 30000); return () => clearInterval(id); }, []);
    const [modal, setModal] = useState(null);
    const [deleteModal, setDeleteModal] = useState(null);
    const [vehicles, setVehicles] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [vendorFleet, setVendorFleet] = useState([]);
    const [allocations, setAllocations] = useState([]);
    const [importing, setImporting] = useState(false);
    const [approvalModal, setApprovalModal] = useState(null);
    const [passengerListModal, setPassengerListModal] = useState(null);
    const [mappings, setMappings] = useState([]);
    const [showImportScreen, setShowImportScreen] = useState(false);
    const [tripSheetModal, setTripSheetModal] = useState(null);

    const getInstanceForTrip = (tripId, date) => {
        if (!instances || !Array.isArray(instances)) return null;
        const targetDate = date || new Date().toISOString().split('T')[0];
        return instances.find(inst => {
            const mId = inst.masterTripId?._id || inst.masterTripId;
            const iDate = inst.tripDate ? String(inst.tripDate) : null;
            return mId === tripId && iDate && iDate.startsWith(targetDate);
        });
    };

    // Work out whether a trip has started on time for a given day.
    // One-time trips use their own trip date; recurring trips use the filter date (or today).
    const getTripTiming = (t, forDate) => {
        const todayLocal = localDateStr(now);
        const dateStr = t.tripFrequency === 'Recurring'
            ? (forDate || todayLocal)
            : (t.tripDate ? localDateStr(t.tripDate) : null);
        if (!dateStr) return { state: 'none' };
        if (PRE_SCHEDULE_STATUSES.includes(t.status)) return { state: 'pending', dateStr };
        const inst = t.tripFrequency === 'Recurring' ? getInstanceForTrip(t._id, dateStr) : null;
        if (t.tripFrequency === 'Recurring' && !inst && !tripRunsOnDate(t, dateStr)) return { state: 'none', dateStr };
        const status = t.tripFrequency === 'Recurring' ? (inst?.status || 'Scheduled') : (t.status || 'Scheduled');
        const start = atTime(dateStr, t.startTime) || new Date(dateStr + 'T00:00:00');
        let end = atTime(dateStr, t.endTime);
        if (end && end <= start) end = new Date(end.getTime() + 86400000);
        const lateMins = Math.max(0, Math.floor((now - start) / 60000));
        const base = { dateStr, status, start, end, lateMins };
        if (status === 'Cancelled') return { ...base, state: 'cancelled' };
        if (status === 'Completed') return { ...base, state: 'completed' };
        if (status === 'Ongoing') {
            if (end && now - end > 15 * 60000) return { ...base, state: 'overrunning', lateMins: Math.floor((now - end) / 60000) };
            return { ...base, state: 'ongoing' };
        }
        // Scheduled (not started)
        if (now < start) return { ...base, state: 'upcoming' };
        if (dateStr < todayLocal || (end && now > end)) return { ...base, state: 'missed' };
        if (lateMins >= 30) return { ...base, state: 'critical' };
        if (lateMins >= 10) return { ...base, state: 'overdue' };
        if (lateMins >= 1) return { ...base, state: 'delayed' };
        return { ...base, state: 'upcoming' };
    };
    const isNotStartedLate = (t) => NOT_STARTED_STATES.includes(getTripTiming(t, filters.date).state)
        || getTripTiming(t, filters.date).state === 'overrunning';

    // ---- Analytics for the selected day (respects client / route / billing filters) ----
    const analytics = (() => {
        const dateStr = filters.date || localDateStr(now);
        const scoped = trips.filter(t =>
            (!filters.client || t.clientId?._id === filters.client || t.clientId === filters.client) &&
            (!filters.route || t.routeId?._id === filters.route || t.routeId === filters.route) &&
            (!filters.billingType || t.billingType === filters.billingType));
        const counts = { total: 0, upcoming: 0, ongoing: 0, completed: 0, cancelled: 0, delayed: 0, overdue: 0, critical: 0, missed: 0, overrunning: 0 };
        let revenue = 0, completedRevenue = 0;
        const alerts = [];
        scoped.forEach(t => {
            if (PRE_SCHEDULE_STATUSES.includes(t.status)) return;
            const runs = tripRunsOnDate(t, dateStr) || (t.tripFrequency === 'Recurring' && !!getInstanceForTrip(t._id, dateStr));
            if (!runs) return;
            const timing = getTripTiming(t, dateStr);
            if (timing.state === 'none' || timing.state === 'pending') return;
            counts.total++;
            counts[timing.state] = (counts[timing.state] || 0) + 1;
            const amt = Number(t.amount) || 0;
            if (timing.state !== 'cancelled') revenue += amt;
            if (timing.state === 'completed') completedRevenue += amt;
            if (TIMING_META[timing.state]) alerts.push({ t, timing });
        });
        // Older one-time trips that were never started / closed
        const backlog = scoped.filter(t => t.tripFrequency !== 'Recurring' && !PRE_SCHEDULE_STATUSES.includes(t.status)
            && ['Scheduled', 'Ongoing'].includes(t.status || 'Scheduled') && t.tripDate && localDateStr(t.tripDate) < dateStr);
        backlog.forEach(t => alerts.push({ t, timing: getTripTiming(t) }));
        alerts.sort((a, b) => (b.timing.lateMins || 0) - (a.timing.lateMins || 0));
        const notStarted = counts.delayed + counts.overdue + counts.critical + counts.missed;
        const active = counts.total - counts.cancelled;
        const dueSoFar = counts.completed + counts.ongoing + counts.overrunning + notStarted;
        return {
            dateStr, counts, revenue, completedRevenue, alerts, backlog: backlog.length, notStarted,
            completionRate: active ? Math.round((counts.completed / active) * 100) : 0,
            startRate: dueSoFar ? Math.round(((counts.completed + counts.ongoing + counts.overrunning) / dueSoFar) * 100) : 100,
        };
    })();

    const handleUpdateInstanceStatus = async (trip, status) => {
        const targetDate = filters.date || new Date().toISOString().split('T')[0];
        try {
            await axios.post(`${API}/trip-instances`, {
                masterTripId: trip._id,
                operatorId,
                tripDate: targetDate,
                status,
                vehicleId: trip.vehicleId?._id || trip.vehicleId,
                driverId: trip.driverId?._id || trip.driverId
            });

            if (trip.tripFrequency !== 'Recurring') {
                await axios.put(`${API}/trips/${trip._id}`, { status });
            }

            fetchData();
        } catch (err) {
            console.error(err);
            alert('Failed to update trip status');
        }
    };
    const fileInputRef = useRef(null);

    const operatorId = user?.operatorId || user?._id || 'all';

    useEffect(() => {
        fetchData();
        fetchCommonData();
    }, []);

    const fetchCommonData = async () => {
        try {
            const [clientRes, routeRes, vehicleRes, driverRes, vendorRes, vfRes, allocRes, instRes, mappingsRes] = await Promise.all([
                axios.get(`${API}/clients/${operatorId}`),
                axios.get(`${API}/routes/${operatorId}`),
                axios.get(`${API}/vehicles/${operatorId}`),
                axios.get(`${API}/drivers/${operatorId}`),
                axios.get(`${API}/vendors/${operatorId}`),
                axios.get(`${API}/vendor-fleet/${operatorId}`),
                axios.get(`${API}/passenger-allocations/${operatorId}`),
                axios.get(`${API}/trip-instances/${operatorId}`),
                axios.get(`${API}/driver-vehicle-mappings/${operatorId}`)
            ]);
            setClients(clientRes.data);
            setRoutes(routeRes.data);
            setVehicles(vehicleRes.data);
            setDrivers(driverRes.data);
            setVendors(vendorRes.data);
            setVendorFleet(vfRes.data);
            setAllocations(allocRes.data);
            setInstances(instRes.data);
            setMappings(mappingsRes.data || []);
        } catch (err) { console.error(err); }
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const [tripRes, allocRes, instRes] = await Promise.all([
                axios.get(`${API}/trips/${operatorId}`),
                axios.get(`${API}/passenger-allocations/${operatorId}`),
                axios.get(`${API}/trip-instances/${operatorId}`)
            ]);
            setTrips(tripRes.data);
            setAllocations(allocRes.data);
            setInstances(instRes.data);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    };

    const downloadTemplate = () => {
        const templateData = [
            {
                'Client': 'Infosys Ltd',
                'Route': 'IT Park Shuttle',
                'Trip Type': 'Login',
                'Frequency': 'Recurring',
                'Recurring Days': 'Mon,Tue,Wed,Thu,Fri',
                'Trip Date': '',
                'Start Time': '08:00',
                'End Time': '09:00',
                'Vehicle Plate': 'DXB-1234',
                'Driver Name': 'John Doe',
                'Amount': 500,
                'Billing Category': 'Daily',
                'Status': 'Scheduled'
            }
        ];
        const ws = XLSX.utils.json_to_sheet(templateData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Trips');
        XLSX.writeFile(wb, 'Trips_Template.xlsx');
    };

    const handleImport = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setImporting(true);
        const reader = new FileReader();
        reader.onload = async (evt) => {
            try {
                const bstr = evt.target.result;
                const wb = XLSX.read(bstr, { type: 'binary' });
                const ws = wb.Sheets[wb.SheetNames[0]];
                const data = XLSX.utils.sheet_to_json(ws);

                const mapped = data.map(row => ({
                    client: row['Client'],
                    route: row['Route'],
                    tripType: row['Trip Type'] || 'Login',
                    tripFrequency: row['Frequency'] || 'One Time',
                    recurringDays: row['Recurring Days'] ? row['Recurring Days'].split(',').map(d => d.trim()) : [],
                    tripDate: row['Trip Date'],
                    startTime: row['Start Time'],
                    endTime: row['End Time'],
                    vehiclePlate: row['Vehicle Plate'],
                    driverName: row['Driver Name'],
                    amount: row['Amount'],
                    billingType: row['Billing Category'] || 'Daily',
                    status: row['Status'] || 'Scheduled'
                }));

                const res = await axios.post(`${API}/trips/import`, { operatorId, trips: mapped });
                if (res.status === 201) {
                    alert(`${res.data.successCount} trips imported!`);
                    fetchData();
                }
            } catch (err) {
                alert('Import failed. Please check your data format.');
            } finally {
                setImporting(false);
                e.target.value = '';
            }
        };
        reader.readAsBinaryString(file);
    };

    const handleApprove = async (trip, extra) => {
        try {
            let rid = trip.routeId?._id || trip.routeId;
            const updatePayload = {
                ...trip,
                proposedPrice: extra.amount,
                vehicleId: trip.requestedVehicleId?._id || trip.requestedVehicleId || trip.vehicleId,
                status: 'PriceProposed'
            };
            
            if (!rid) {
                // Defer route creation until the client accepts the proposal
                updatePayload.tripName = extra.routeName;
                updatePayload.totalDistance = extra.distance || 0;
            } else {
                updatePayload.routeId = rid;
            }
            
            await axios.put(`${API}/trips/${trip._id}`, updatePayload);
            alert('Price proposal sent to client successfully!');
            setApprovalModal(null);
            fetchData();
        } catch (err) {
            console.error('Error approving trip:', err);
            alert('Failed to approve trip request.');
        }
    };

    const acceptVendorProposal = async (trip) => {
        try {
            await axios.put(`${API}/trips/${trip._id}`, {
                vendorStatus: 'Accepted',
                vehicleId: trip.vendorProposedVehicleId?._id || trip.vendorProposedVehicleId,
                driverId: trip.vendorProposedDriverId?._id || trip.vendorProposedDriverId,
                status: 'Scheduled'
            });
            fetchData();
        } catch (err) { console.error(err); alert('Failed to accept proposal'); }
    };

    const rejectVendorProposal = async (trip) => {
        try {
            await axios.put(`${API}/trips/${trip._id}`, { vendorStatus: 'Rejected' });
            fetchData();
        } catch (err) { console.error(err); alert('Failed to reject proposal'); }
    };

    const handleSave = async (formData) => {
        try {
            if (modal.data?._id && !modal.isDispatchOnly && modal.data?.vehicleId && !formData.changeReason) {
                alert("Please provide a status or reason for changing this trip schedule.");
                return;
            }
            // Clean payload
            const payload = { ...formData, operatorId, operatorName: operatorDetails?.companyName || 'Operator Admin' };

            // Ensure all ID fields are either a valid ID or null/removed (Mongoose fails on empty strings)
            const sanitizeId = (id) => {
                if (!id) return null;
                if (typeof id === 'object') return id._id || null;
                return id === '' ? null : id;
            };

            payload.clientId = sanitizeId(payload.clientId);
            payload.routeId = sanitizeId(payload.routeId);
            payload.vehicleId = sanitizeId(payload.vehicleId);
            payload.driverId = sanitizeId(payload.driverId);
            payload.vendorId = sanitizeId(payload.vendorId);

            // Clean up tripDate (Mongoose rejects empty strings for Date fields)
            if (!payload.tripDate || payload.tripFrequency === 'Recurring') {
                payload.tripDate = null;
            }
            if (!payload.startDate) payload.startDate = null;
            if (!payload.endDate) payload.endDate = null;

            // Remove Mongoose-specific meta fields if they exist
            delete payload.__v;
            delete payload.createdAt;
            delete payload.updatedAt;

            // If finalizing a client-accepted trip, move to Scheduled
            if (payload.status === 'ClientAccepted') {
                payload.status = 'Scheduled';
            }

            if (modal.data?._id) {
                await axios.put(`${API}/trips/${modal.data._id}`, payload);
                alert('Trip schedule updated successfully!');
            } else {
                // Remove _id if it somehow leaked into new form state
                delete payload._id;
                await axios.post(`${API}/trips`, payload);
                clearTripDraft();
                alert('Trip schedule created successfully!');
            }
            setModal(null);
            fetchData();
        } catch (err) {
            console.error('Error saving trip:', err);
            let msg = err.response?.data?.message || err.message;
            if (msg.includes('Trip validation failed')) {
                const parts = [];
                if (msg.includes('dropLocation')) parts.push('Drop Location');
                if (msg.includes('pickupLocation')) parts.push('Pickup Location');
                if (parts.length > 0) {
                    msg = `Missing required fields: ${parts.join(' & ')}. Please select a route or enter pickup and drop locations.`;
                }
            }
            alert(`Failed to save trip: ${msg}`);
        }
    };

    const handleDelete = async () => {
        try {
            await axios.delete(`${API}/trips/${deleteModal._id}`);
            setDeleteModal(null);
            fetchData();
        } catch (err) {
            console.error(err);
            alert('Error deleting trip');
        }
    };

    const filtered = trips.filter(t => {
        const matchesSearch = (
            t.routeId?.name?.toLowerCase().includes(search.toLowerCase()) ||
            t.clientId?.companyName?.toLowerCase().includes(search.toLowerCase()) ||
            t.vendorId?.vendorName?.toLowerCase().includes(search.toLowerCase()) ||
            t.passengerName?.toLowerCase().includes(search.toLowerCase()) ||
            t.passengerPhone?.includes(search)
        );
        const targetDateForFilter = filters.date || new Date().toISOString().split('T')[0];
        const tripInstForFilter = getInstanceForTrip(t._id, targetDateForFilter);
        const tripAllocations = allocations.filter(a => {
            if ((a.tripId?._id || a.tripId) !== t._id) return false;
            if (t.billingType === 'Shuttle with Booking' && t.tripFrequency !== 'One Time') {
                if (!a.tripInstanceId) return true;
                if (tripInstForFilter) {
                    return (a.tripInstanceId?._id || a.tripInstanceId) === tripInstForFilter._id;
                }
                return false;
            }
            return true;
        });
        const femaleCount = tripAllocations.filter(a => a.passengerId?.gender === 'Female').length;
        const startTimeHour = t.startTime ? parseInt(t.startTime.split(':')[0]) : -1;
        const isNightTrip = startTimeHour >= 18 || startTimeHour < 5;
        const safetyRisk = isNightTrip && femaleCount > 0;

        return matchesSearch && 
               (!filters.client || t.clientId?._id === filters.client || t.clientId === filters.client) &&
               (!filters.route || t.routeId?._id === filters.route || t.routeId === filters.route) &&
               (!filters.billingType || t.billingType === filters.billingType) &&
               (!filters.date || (filters.overdueOnly && t.tripFrequency !== 'Recurring') || (t.tripFrequency === 'Recurring' ? (
                   // only dates inside the trip's start-end range and on its weekdays
                   tripRunsOnDate(t, filters.date) || !!getInstanceForTrip(t._id, filters.date)
               ) : (t.tripDate && t.tripDate.startsWith(filters.date)))) &&
               (!filters.safetyOnly || safetyRisk) &&
               (!filters.overdueOnly || isNotStartedLate(t));
    });

    if (showImportScreen) {
        return (
            <ImportWizard
                trips={trips}
                operatorId={operatorId}
                onBack={() => setShowImportScreen(false)}
                onDone={() => { setShowImportScreen(false); fetchData(); }}
            />
        );
    }

    return (
        <div className="md-page">
            <div className="md-header">
                <div>
                    <h1 className="md-title">Trips Management</h1>
                    <p className="md-subtitle">Schedule and optimize vehicle trips for clients.</p>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                    <button className="premium-btn btn-import" onClick={() => setShowImportScreen(true)}>
                        <Icon d={icons.upload} size={18} strokeWidth={2.5} />
                        Import
                    </button>
                    <button className="premium-btn btn-template" onClick={downloadTemplate}>
                        <Icon d={icons.download} size={18} strokeWidth={2.5} />
                        Template
                    </button>
                    <button className="premium-btn btn-primary-action" onClick={() => setModal({ data: null })} title={loadTripDraft() ? 'You have an unsaved trip draft' : undefined}>
                        <Icon d={icons.plus} size={18} strokeWidth={2.5} />
                        Add Trip
                        {!modal && loadTripDraft() && (
                            <span style={{ marginLeft: 6, background: '#f59e0b', color: 'white', fontSize: 10, fontWeight: 800, padding: '2px 7px', borderRadius: 10, letterSpacing: '0.03em' }}>DRAFT</span>
                        )}
                    </button>
                </div>
            </div>

            {/* Pending Requests Notification Bar */}
            {trips.filter(t => t.status === 'Requested').length > 0 && (
                <div style={{
                    marginBottom: 24, padding: '16px 24px', borderRadius: 16,
                    background: 'linear-gradient(90deg, #063A5C 0%, #312e81 100%)',
                    color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    boxShadow: '0 10px 20px -5px rgba(6, 58, 92, 0.3)', animation: 'slideIn 0.5s ease'
                }}>
                    <style>{`@keyframes slideIn { from { transform: translateY(-20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}</style>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 15 }}>
                        <div style={{ width: 42, height: 42, borderRadius: '50%', background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Icon d={icons.map} size={20} />
                        </div>
                        <div>
                            <div style={{ fontSize: 16, fontWeight: 800 }}>{trips.filter(t => t.status === 'Requested').length} Pending Requests</div>
                            <div style={{ fontSize: 13, opacity: 0.8, fontWeight: 500 }}>Clients have submitted new trip requirements awaiting your approval.</div>
                        </div>
                    </div>
                    <button
                        onClick={() => {
                            const firstReq = trips.find(t => t.status === 'Requested');
                            setApprovalModal(firstReq);
                        }}
                        style={{ padding: '10px 20px', borderRadius: 12, border: 'none', background: 'white', color: '#063A5C', fontWeight: 800, fontSize: 13, cursor: 'pointer' }}
                    >
                        Review Latest Request
                    </button>
                </div>
            )}

            {/* Vendor Proposal Alert Banner */}
            {trips.filter(t => t.vendorStatus === 'Vendor Proposed').length > 0 && (
                <div style={{
                    marginBottom: 24, padding: '16px 24px', borderRadius: 16,
                    background: 'linear-gradient(90deg, #065f46 0%, #059669 100%)',
                    color: 'white', display: 'flex', alignItems: 'center', gap: 15,
                    boxShadow: '0 10px 20px -5px rgba(6,95,70,0.35)'
                }}>
                    <div style={{ width: 42, height: 42, borderRadius: '50%', background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Icon d={icons.truck} size={22} />
                    </div>
                    <div>
                        <div style={{ fontSize: 16, fontWeight: 800 }}>
                            {trips.filter(t => t.vendorStatus === 'Vendor Proposed').length} Vendor Proposal{trips.filter(t => t.vendorStatus === 'Vendor Proposed').length > 1 ? 's' : ''} Awaiting Your Approval
                        </div>
                        <div style={{ fontSize: 13, opacity: 0.85, fontWeight: 500 }}>
                            Vendor(s) have submitted vehicle, driver and pricing — review and accept in the Vehicle & Driver column below.
                        </div>
                    </div>
                </div>
            )}

            {/* ===== Trip Analytics & Overdue Monitor ===== */}
            {!loading && (() => {
                const a = analytics;
                const c = a.counts;
                const isToday = a.dateStr === localDateStr(now);
                const dayLabel = isToday ? 'Today' : new Date(a.dateStr + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
                const cards = [
                    { k: 'Total Trips', v: c.total, sub: dayLabel, color: '#063A5C', bg: '#e6f0fa' },
                    { k: 'Completed', v: c.completed, sub: `${a.completionRate}% completion`, color: '#16a34a', bg: '#dcfce7' },
                    { k: 'Ongoing', v: c.ongoing + c.overrunning, sub: c.overrunning ? `${c.overrunning} overrunning` : 'On the road', color: '#2563eb', bg: '#dbeafe' },
                    { k: 'Upcoming', v: c.upcoming, sub: 'Not yet due', color: '#0891b2', bg: '#cffafe' },
                    { k: 'Not Started', v: a.notStarted, sub: 'Start time crossed', color: '#dc2626', bg: '#fee2e2', alert: a.notStarted > 0, onClick: () => setFilters(f => ({ ...f, overdueOnly: !f.overdueOnly })) },
                    { k: 'Cancelled', v: c.cancelled, sub: '—', color: '#64748b', bg: '#f1f5f9' },
                    { k: 'On-time Start', v: `${a.startRate}%`, sub: 'of trips due so far', color: a.startRate >= 90 ? '#16a34a' : a.startRate >= 70 ? '#d97706' : '#dc2626', bg: '#f8fafc' },
                    { k: 'Trip Value', v: formatCurrency(a.revenue, operatorDetails?.country), sub: `${formatCurrency(a.completedRevenue, operatorDetails?.country)} earned`, color: '#059669', bg: '#ecfdf5' },
                ];
                const seg = [
                    { n: c.completed, color: '#16a34a', label: 'Completed' },
                    { n: c.ongoing, color: '#2563eb', label: 'Ongoing' },
                    { n: c.overrunning, color: '#7c3aed', label: 'Overrunning' },
                    { n: c.upcoming, color: '#06b6d4', label: 'Upcoming' },
                    { n: c.delayed + c.overdue, color: '#f59e0b', label: 'Delayed / Overdue' },
                    { n: c.critical + c.missed, color: '#dc2626', label: 'Critical / Missed' },
                    { n: c.cancelled, color: '#cbd5e1', label: 'Cancelled' },
                ].filter(x => x.n > 0);
                return (
                    <div className="md-panel" style={{ marginBottom: 20, padding: 20 }}>
                        <style>{`@keyframes odPulse { 0%,100% { box-shadow: 0 0 0 0 rgba(220,38,38,.35); } 50% { box-shadow: 0 0 0 6px rgba(220,38,38,0); } }`}</style>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: showAnalytics ? 16 : 0 }}>
                            <div>
                                <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>Trip Analytics · {dayLabel}</div>
                                <div style={{ fontSize: 12, color: '#64748b' }}>
                                    Live status of trips for the selected date · updated {now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                                </div>
                            </div>
                            <button onClick={() => setShowAnalytics(v => !v)} style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc', fontSize: 12, fontWeight: 700, color: '#475569', cursor: 'pointer' }}>
                                {showAnalytics ? 'Hide' : 'Show'}
                            </button>
                        </div>
                        {showAnalytics && (
                            <>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
                                    {cards.map(card => (
                                        <div key={card.k} onClick={card.onClick}
                                            style={{
                                                padding: '14px 16px', borderRadius: 12, background: card.bg,
                                                border: card.alert ? '1.5px solid #fca5a5' : '1px solid rgba(15,23,42,0.06)',
                                                cursor: card.onClick ? 'pointer' : 'default',
                                                animation: card.alert ? 'odPulse 2s infinite' : 'none'
                                            }}
                                            title={card.onClick ? 'Click to show only these trips' : undefined}>
                                            <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{card.k}</div>
                                            <div style={{ fontSize: 24, fontWeight: 800, color: card.color, marginTop: 4, lineHeight: 1.1 }}>{card.v}</div>
                                            <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>{card.sub}</div>
                                        </div>
                                    ))}
                                </div>

                                {c.total > 0 && (
                                    <div style={{ marginTop: 16 }}>
                                        <div style={{ display: 'flex', height: 10, borderRadius: 6, overflow: 'hidden', background: '#f1f5f9' }}>
                                            {seg.map(x => <div key={x.label} title={`${x.label}: ${x.n}`} style={{ width: `${(x.n / c.total) * 100}%`, background: x.color }} />)}
                                        </div>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginTop: 8 }}>
                                            {seg.map(x => (
                                                <span key={x.label} style={{ fontSize: 11, color: '#475569', display: 'flex', alignItems: 'center', gap: 5 }}>
                                                    <span style={{ width: 8, height: 8, borderRadius: 2, background: x.color }} />{x.label} <b>{x.n}</b>
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {a.alerts.length > 0 && (
                                    <div style={{ marginTop: 16, border: '1px solid #fecaca', borderRadius: 12, overflow: 'hidden' }}>
                                        <div style={{ padding: '10px 14px', background: '#fef2f2', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div style={{ fontSize: 13, fontWeight: 800, color: '#991b1b' }}>
                                                ⚠ {a.alerts.length} trip{a.alerts.length > 1 ? 's' : ''} need attention
                                                {a.backlog > 0 && <span style={{ fontWeight: 600, color: '#b91c1c' }}> · {a.backlog} from earlier dates still open</span>}
                                            </div>
                                            <button onClick={() => setFilters(f => ({ ...f, overdueOnly: !f.overdueOnly }))}
                                                style={{ padding: '5px 12px', borderRadius: 8, border: 'none', background: filters.overdueOnly ? '#991b1b' : '#dc2626', color: 'white', fontSize: 11, fontWeight: 800, cursor: 'pointer' }}>
                                                {filters.overdueOnly ? 'Show all trips' : 'Show only these'}
                                            </button>
                                        </div>
                                        {a.alerts.slice(0, 6).map(({ t, timing }) => {
                                            const meta = TIMING_META[timing.state] || TIMING_META.missed;
                                            const inst = t.tripFrequency === 'Recurring' ? getInstanceForTrip(t._id, timing.dateStr) : null;
                                            const driver = inst?.driverId || t.driverId;
                                            const vehicle = inst?.vehicleId || t.vehicleId;
                                            return (
                                                <div key={t._id + (timing.dateStr || '')} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderTop: '1px solid #fee2e2', background: 'white', flexWrap: 'wrap' }}>
                                                    <span style={{ padding: '3px 8px', borderRadius: 6, background: meta.bg, color: meta.color, fontSize: 10, fontWeight: 800, textTransform: 'uppercase', minWidth: 78, textAlign: 'center' }}>{meta.label}</span>
                                                    <div style={{ flex: 1, minWidth: 160 }}>
                                                        <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{t.routeId?.name || t.tripName || 'Trip'} <span style={{ fontWeight: 500, color: '#64748b' }}>· {t.clientId?.companyName || '—'}</span></div>
                                                        <div style={{ fontSize: 11, color: '#64748b' }}>
                                                            {new Date(timing.dateStr + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                                                            {' · '}{timing.start ? timing.start.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '—'}
                                                            {' · '}{vehicle?.registrationNumber || vehicle?.plateNumber || 'No vehicle'} / {driver?.name || 'No driver'}
                                                        </div>
                                                    </div>
                                                    <div style={{ fontSize: 12, fontWeight: 800, color: meta.color, whiteSpace: 'nowrap' }}>
                                                        {timing.state === 'overrunning' ? `Past end by ${fmtLate(timing.lateMins)}` : `Late by ${fmtLate(timing.lateMins)}`}
                                                    </div>
                                                    {(driver?.phoneNumber || driver?.phone) && (
                                                        <a href={`tel:${driver.phoneNumber || driver.phone}`} style={{ padding: '5px 10px', borderRadius: 8, background: '#e6f0fa', color: '#063A5C', fontSize: 11, fontWeight: 800, textDecoration: 'none' }}>Call driver</a>
                                                    )}
                                                </div>
                                            );
                                        })}
                                        {a.alerts.length > 6 && (
                                            <div style={{ padding: '8px 14px', fontSize: 11, color: '#991b1b', background: '#fff7f7', borderTop: '1px solid #fee2e2' }}>+ {a.alerts.length - 6} more — click “Show only these” to see all</div>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                );
            })()}

            <div className="md-panel">

                <div className="md-panel-toolbar" style={{ flexWrap: 'wrap', gap: '15px' }}>
                    <div className="md-search-wrap" style={{ minWidth: '250px' }}>
                        <Icon d={icons.search} size={14} />
                        <input
                            className="md-input md-search"
                            placeholder="Search trips..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                    </div>
                    <div style={{ display: 'flex', gap: '10px', flex: 1 }}>
                        <select className="md-input" value={filters.client} onChange={e => setFilters({ ...filters, client: e.target.value })} style={{ height: '40px' }}>
                            <option value="">All Clients</option>
                            {clients.map(c => <option key={c._id} value={c._id}>{c.companyName}</option>)}
                        </select>
                        <select className="md-input" value={filters.billingType} onChange={e => setFilters({ ...filters, billingType: e.target.value })} style={{ height: '40px' }}>
                            <option value="">All Billing</option>
                            <option value="Ad-hoc">Adhoc</option>
                            <option value="Daily">Daily</option>
                            <option value="Monthly">Monthly</option>
                            <option value="Shuttle">Shuttle</option>
                            <option value="Shuttle with Booking">Shuttle with Booking</option>
                        </select>
                        <select className="md-input" value={filters.route} onChange={e => setFilters({ ...filters, route: e.target.value })} style={{ height: '40px' }}>
                            <option value="">All Routes</option>
                            {routes.map(r => <option key={r._id} value={r._id}>{r.name}</option>)}
                        </select>
                        <input type="date" className="md-input" value={filters.date} onChange={e => setFilters({ ...filters, date: e.target.value })} style={{ height: '40px' }} />
                            <label style={{ 
                                display: 'flex', alignItems: 'center', gap: 8, padding: '0 15px', 
                                borderRadius: 10, border: '1px solid #e2e8f0', background: filters.safetyOnly ? '#fce7f3' : '#f8fafc',
                                cursor: 'pointer', userSelect: 'none', transition: 'all 0.2s', minWidth: '130px',
                                borderColor: filters.safetyOnly ? '#C93269' : '#e2e8f0'
                            }}>
                            <input 
                                type="checkbox" 
                                checked={filters.safetyOnly} 
                                onChange={e => setFilters({ ...filters, safetyOnly: e.target.checked })}
                                style={{ cursor: 'pointer', width: 16, height: 16 }}
                            />
                            <span style={{ fontSize: 13, fontWeight: 700, color: filters.safetyOnly ? '#be185d' : '#64748b' }}>Safety Only</span>
                        </label>
                        <label style={{
                            display: 'flex', alignItems: 'center', gap: 8, padding: '0 15px',
                            borderRadius: 10, border: '1px solid', background: filters.overdueOnly ? '#fee2e2' : '#f8fafc',
                            cursor: 'pointer', userSelect: 'none', minWidth: '140px',
                            borderColor: filters.overdueOnly ? '#dc2626' : '#e2e8f0'
                        }}>
                            <input
                                type="checkbox"
                                checked={filters.overdueOnly}
                                onChange={e => setFilters({ ...filters, overdueOnly: e.target.checked })}
                                style={{ cursor: 'pointer', width: 16, height: 16 }}
                            />
                            <span style={{ fontSize: 13, fontWeight: 700, color: filters.overdueOnly ? '#b91c1c' : '#64748b' }}>Overdue Only</span>
                        </label>
                    </div>
                </div>

                {loading ? (
                    <div style={{ padding: 60, textAlign: 'center', color: '#94a3b8' }}>Loading trips...</div>
                ) : (
                    <div className="md-table-wrap">
                        <table className="md-table">
                            <thead>
                                <tr>
                                    <th className="md-th">Client</th>
                                    <th className="md-th">Route Details</th>
                                    <th className="md-th">KM (Mileage)</th>
                                    <th className="md-th">Passengers</th>
                                    <th className="md-th">Vehicle & Driver</th>
                                    <th className="md-th">Schedule</th>
                                    <th className="md-th">Trip Fee</th>
                                    <th className="md-th">Status</th>
                                    <th className="md-th">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map(t => {
                                    const targetDateForMap = filters.date || new Date().toISOString().split('T')[0];
                                    const tripInstForMap = getInstanceForTrip(t._id, targetDateForMap);
                                    const tripAllocations = allocations.filter(a => {
                                        if ((a.tripId?._id || a.tripId) !== t._id) return false;
                                        if (t.billingType === 'Shuttle with Booking' && t.tripFrequency !== 'One Time') {
                                            if (!a.tripInstanceId) return true;
                                            if (tripInstForMap) {
                                                return (a.tripInstanceId?._id || a.tripInstanceId) === tripInstForMap._id;
                                            }
                                            return false;
                                        }
                                        return true;
                                    });
                                    const femaleCount = tripAllocations.filter(a => a.passengerId?.gender === 'Female').length;
                                    const startTimeHour = t.startTime ? parseInt(t.startTime.split(':')[0]) : -1;
                                    const isNightTrip = startTimeHour >= 18 || startTimeHour < 5;
                                    const safetyRisk = isNightTrip && femaleCount > 0;
                                    const routeObj = routes.find(r => r._id === (t.routeId?._id || t.routeId));
                                    const displayDistance = routeObj?.distance || t.routeId?.distance || t.totalDistance || t.distance || 0;
                                    const rowTiming = getTripTiming(t, filters.date);
                                    const rowLate = !!TIMING_META[rowTiming.state];
                                    return (
                                        <tr key={t._id} style={{ background: safetyRisk ? '#fce7f3' : rowLate ? '#fff5f5' : 'transparent', boxShadow: rowLate ? `inset 3px 0 0 ${TIMING_META[rowTiming.state].color}` : 'none' }}>
                                        <td className="md-td" style={{ position: 'relative', overflow: 'hidden' }}>
                                            {safetyRisk && (
                                                <div style={{
                                                    position: 'absolute', top: 0, left: 0, width: 45, height: 45,
                                                    overflow: 'hidden', zIndex: 2, pointerEvents: 'none'
                                                }}>
                                                    <div style={{
                                                        position: 'absolute', top: 6, left: -14, width: 60,
                                                        background: '#C93269', color: 'white', fontSize: 7, fontWeight: 900,
                                                        textAlign: 'center', transform: 'rotate(-45deg)',
                                                        boxShadow: '0 2px 5px rgba(0,0,0,0.2)', textTransform: 'uppercase',
                                                        letterSpacing: '0.05em', padding: '1px 0'
                                                    }}>
                                                        SAFETY
                                                    </div>
                                                </div>
                                            )}
                                            {t.tripType === 'on_demand' ? (
                                                <div>
                                                    <div style={{ color: '#16539C', fontSize: 11, fontWeight: 800, textTransform: 'uppercase', marginBottom: 2 }}>{t.passengerName || 'Adhoc Guest'}</div>
                                                    <div style={{ fontWeight: 700, color: '#063A5C' }}>{t.clientId?.companyName || '—'}</div>
                                                    <div style={{ fontSize: 10, color: '#94a3b8' }}>ID: {t._id.slice(-6).toUpperCase()}{t.passengerPhone ? ` • ${t.passengerPhone}` : ''}</div>
                                                </div>
                                            ) : (
                                                <div>
                                                    {t.tripName && <div style={{ color: '#11427D', fontSize: 11, fontWeight: 800, textTransform: 'uppercase', marginBottom: 2 }}>{t.tripName}</div>}
                                                    <div style={{ fontWeight: 700, color: '#063A5C' }}>{t.clientId?.companyName || '—'}</div>
                                                    <div style={{ fontSize: 10, color: '#94a3b8' }}>ID: {t._id.slice(-6).toUpperCase()}</div>
                                                </div>
                                            )}
                                        </td>
                                        <td className="md-td">
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <div style={{
                                                    padding: '4px 8px', borderRadius: 6,
                                                    background: t.billingType?.includes('Shuttle') ? '#f0fdf4' : (t.billingType === 'Ad-hoc' ? '#fef2f2' : '#f1f5f9'),
                                                    color: t.billingType?.includes('Shuttle') ? '#166534' : (t.billingType === 'Ad-hoc' ? '#991b1b' : '#64748b'),
                                                    fontSize: 10, fontWeight: 700
                                                }}>
                                                    {t.billingType || 'Daily'}
                                                </div>
                                                <div style={{
                                                    padding: '4px 8px', borderRadius: 6,
                                                    background: t.tripType === 'on_demand' ? '#e6f0fa' : '#f1f5f9',
                                                    color: t.tripType === 'on_demand' ? '#11427D' : '#64748b',
                                                    fontSize: 10, fontWeight: 700
                                                }}>
                                                    {t.tripType === 'on_demand' ? 'Adhoc' : (t.tripType || 'Fixed')}
                                                </div>
                                                <strong>{t.tripType === 'on_demand' ? 'Adhoc Trip' : (t.routeId?.name || '—')}</strong>
                                            </div>
                                            <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                                                {t.tripType === 'on_demand' ? `${t.pickupLocation} ➔ ${t.dropLocation}` : t.tripFrequency}
                                            </div>
                                        </td>
                                        <td className="md-td">
                                            <div style={{ fontWeight: 700, color: '#1e293b', fontSize: 12 }}>
                                                {displayDistance} km
                                            </div>
                                        </td>
                                        <td className="md-td">
                                            {(() => {
                                                const targetDateForTD = filters.date || new Date().toISOString().split('T')[0];
                                                const tripInstForTD = getInstanceForTrip(t._id, targetDateForTD);
                                                const tripAllocations = allocations.filter(a => {
                                                    if ((a.tripId?._id || a.tripId) !== t._id) return false;
                                                    if (t.billingType === 'Shuttle with Booking' && t.tripFrequency !== 'One Time') {
                                                        if (!a.tripInstanceId) return true;
                                                        if (tripInstForTD) {
                                                            return (a.tripInstanceId?._id || a.tripInstanceId) === tripInstForTD._id;
                                                        }
                                                        return false;
                                                    }
                                                    return true;
                                                });
                                                 const maleCount = tripAllocations.filter(a => a.passengerId?.gender === 'Male').length;
                                                 const count = tripAllocations.length > 0 ? tripAllocations.length : (t.tripType === 'on_demand' ? 1 : 0);
                                                 const capacity = t.vehicleId?.seatingCapacity || 0;
                                                 const isFull = capacity > 0 && count >= capacity;
                                                 return (
                                                     <div 
                                                        style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: count > 0 ? 'pointer' : 'default' }}
                                                        onClick={() => count > 0 && setPassengerListModal({ tripId: t._id, allocations: tripAllocations, date: filters.date })}
                                                        title={count > 0 ? "Click to view passenger list" : ""}
                                                     >
                                                         <div style={{
                                                             width: 32, height: 32, borderRadius: 10,
                                                             background: count === 0 ? '#f8fafc' : (isFull ? '#fef2f2' : '#f0f9ff'),
                                                             color: count === 0 ? '#94a3b8' : (isFull ? '#ef4444' : '#0284c7'),
                                                             display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                             fontWeight: 800, fontSize: 13, border: `1px solid ${count === 0 ? '#e2e8f0' : (isFull ? '#fecaca' : '#bae6fd')}`
                                                         }}>
                                                             {count}
                                                         </div>
                                                         <div>
                                                             <div style={{ fontWeight: 700, fontSize: 12, color: count === 0 ? '#94a3b8' : '#1e293b' }}>
                                                                 {count} {count === 1 ? 'Passenger' : 'Passengers'}
                                                             </div>
                                                             <div style={{ fontSize: 10, color: '#94a3b8', display: 'flex', gap: 6 }}>

                                                                  {(maleCount > 0 || femaleCount > 0) && (
                                                                     <>
                                                                         {maleCount > 0 && <span style={{ color: '#0284c7' }}>M: {maleCount}</span>}
                                                                         {femaleCount > 0 && <span style={{ color: '#db2777' }}>F: {femaleCount}</span>}
                                                                     </>
                                                                 )}
                                                             </div>
                                                         </div>
                                                     </div>
                                                 );
                                            })()}
                                        </td>
                                        <td className="md-td">
                                            {(() => {
                                                const inst = getInstanceForTrip(t._id, filters.date);
                                                const vehicle = inst?.vehicleId || t.vehicleId;
                                                const driver = inst?.driverId || t.driverId;

                                                if (t.vendorId && t.vendorStatus === 'Vendor Proposed') {
                                                    return (
                                                        <div style={{ background: '#eff6ff', borderRadius: 10, padding: '10px 12px', border: '1.5px solid #bfdbfe' }}>
                                                            <div style={{ fontSize: 10, fontWeight: 800, color: '#1d4ed8', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: 4 }}><Icon d={icons.truck} size={10} /> Vendor Proposal</div>
                                                            <div style={{ fontSize: 12, fontWeight: 700, color: '#1e40af' }}>{t.vendorId?.vendorName}</div>
                                                            <div style={{ fontSize: 11, color: '#374151', marginTop: 3, display: 'flex', alignItems: 'center', gap: 4 }}><Icon d={icons.truck} size={11} /> {t.vendorProposedVehicleId?.registrationNumber || '—'} {t.vendorProposedVehicleId?.vehicleCode ? `(${t.vendorProposedVehicleId.vehicleCode})` : ''}</div>
                                                            <div style={{ fontSize: 11, color: '#374151', display: 'flex', alignItems: 'center', gap: 4 }}><Icon d={icons.user} size={11} /> {t.vendorProposedDriverId?.name || '—'} {t.vendorProposedDriverId?.driverCode ? `(${t.vendorProposedDriverId.driverCode})` : ''}</div>
                                                            <div style={{ fontSize: 12, fontWeight: 800, color: '#059669', marginTop: 3 }}>{t.vendorProposedAmount ? formatCurrency(t.vendorProposedAmount, operatorDetails?.country) : '—'}</div>
                                                            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                                                                <button
                                                                    onClick={() => acceptVendorProposal(t)}
                                                                    style={{ flex: 1, padding: '5px 0', background: '#10b981', color: 'white', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}
                                                                ><Icon d={icons.check} size={11} color="white" /> Accept</button>
                                                                <button
                                                                    onClick={() => rejectVendorProposal(t)}
                                                                    style={{ flex: 1, padding: '5px 0', background: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}
                                                                ><Icon d={icons.x} size={11} /> Reject</button>
                                                            </div>
                                                        </div>
                                                    );
                                                }
                                                if (t.vendorId) {
                                                    return (
                                                        <div>
                                                            <div style={{ fontWeight: 600, color: '#16539C' }}>{t.vendorId.vendorName}</div>
                                                            <div style={{ fontSize: 11, color: '#94a3b8' }}>
                                                                {t.vendorStatus === 'Accepted' ? `✓ ${vehicle?.vehicleCode || vehicle?.registrationNumber || t.vendorProposedVehicleId?.vehicleCode || t.vendorProposedVehicleId?.registrationNumber || 'No Vehicle'}` :
                                                                    t.vendorStatus === 'Rejected' ? '✗ Proposal Rejected' :
                                                                        'Awaiting vendor response...'}
                                                            </div>
                                                        </div>
                                                    );
                                                }
                                                if (vehicle) {
                                                    return (
                                                        <div>
                                                            <div style={{ fontWeight: 600 }}>{vehicle.plateNumber || vehicle.registrationNumber || '—'} {vehicle.vehicleCode ? `(${vehicle.vehicleCode})` : ''}</div>
                                                            <div style={{ fontSize: 11, color: '#64748b' }}>{driver?.name || 'No Driver'} {driver?.driverCode ? `(${driver.driverCode})` : ''}</div>
                                                        </div>
                                                    );
                                                }
                                                if (t.requestedVehicleId && (t.status === 'Requested' || t.status === 'PriceProposed')) {
                                                    return (
                                                        <div style={{ background: '#eef2ff', padding: '6px 8px', borderRadius: 8, border: '1px solid #c3dcf5' }}>
                                                            <div style={{ fontSize: 9, fontWeight: 800, color: '#16539C', textTransform: 'uppercase', marginBottom: 2 }}>Client Requested</div>
                                                            <div style={{ fontWeight: 700, fontSize: 11, color: '#1e293b' }}>{t.requestedVehicleId.make} {t.requestedVehicleId.model}</div>
                                                            <div style={{ fontSize: 10, color: '#64748b' }}>{t.requestedVehicleId.seatingCapacity} Seats</div>
                                                        </div>
                                                    );
                                                }
                                                if (!vehicle && !['Requested', 'PriceProposed', 'Cancelled'].includes(t.status)) {
                                                    return (
                                                        <div style={{ padding: '4px 0' }}>
                                                            <div style={{ color: '#ef4444', fontSize: 11, fontWeight: 800, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                                                                <Icon d={icons.truck} size={12} /> Pending Assignment
                                                            </div>
                                                            <button 
                                                                onClick={() => setModal({ data: t, isDispatchOnly: true })}
                                                                style={{ 
                                                                    display: 'inline-flex', alignItems: 'center', gap: 6, 
                                                                    padding: '7px 14px', borderRadius: 8, border: 'none', 
                                                                    background: 'linear-gradient(135deg, #16539C, #00B2A9)', 
                                                                    color: 'white', fontSize: 11, fontWeight: 800, 
                                                                    cursor: 'pointer', boxShadow: '0 4px 10px rgba(79,70,229,0.25)', 
                                                                    transition: 'transform 0.15s ease' 
                                                                }}
                                                                onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.03)'}
                                                                onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                                                            >
                                                                Assign Fleet
                                                            </button>
                                                        </div>
                                                    );
                                                }
                                                return <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>Unassigned</span>;
                                            })()}
                                        </td>
                                        <td className="md-td">
                                            <div style={{ fontSize: 12 }}>
                                                {t.tripFrequency === 'One Time' ? (
                                                    <div style={{ fontWeight: 700, color: '#1e293b' }}>
                                                        {t.tripDate ? new Date(t.tripDate).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—'}
                                                    </div>
                                                ) : (
                                                    <div>
                                                        <div style={{ fontSize: 10, color: '#64748b', fontWeight: 700, marginBottom: 5 }}>
                                                            {t.startDate ? new Date(t.startDate).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'Start'} 
                                                            {t.endDate ? ` to ${new Date(t.endDate).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}` : ' onwards'}
                                                        </div>
                                                        <div style={{ display: 'flex', gap: 4 }}>
                                                            {t.recurringDays?.map(d => (
                                                                <span key={d} style={{ background: '#e6f0fa', color: '#11427D', padding: '2px 4px', borderRadius: 4, fontSize: 10, fontWeight: 700 }}>{d.charAt(0)}</span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            {t.startTime && (
                                                <div style={{ fontSize: 11, color: '#063A5C', fontWeight: 700, marginTop: 4 }}>
                                                    {t.startTime} {t.endTime ? `- ${t.endTime}` : ''}
                                                </div>
                                            )}
                                        </td>
                                        <td className="md-td">
                                            <div style={{ fontWeight: 700, color: '#10b981', fontSize: 13 }}>
                                                {t.amount ? formatCurrency(t.amount, operatorDetails?.country) : (t.proposedPrice ? formatCurrency(t.proposedPrice, operatorDetails?.country) : '—')}
                                                {(!t.amount && t.proposedPrice && !['Scheduled', 'Completed', 'Cancelled'].includes(t.status)) && (
                                                    <span style={{ fontSize: 9, color: '#94a3b8', marginLeft: 4, fontWeight: 600, textTransform: 'uppercase' }}>(Proposed)</span>
                                                )}
                                            </div>
                                            {t.vendorId && (t.vendorPrice || t.vendorProposedAmount) && (
                                                <div style={{ fontSize: 11, color: '#16539C', marginTop: 4, fontWeight: 700 }}>
                                                    Vendor: {formatCurrency(t.vendorPrice || t.vendorProposedAmount, operatorDetails?.country)}
                                                </div>
                                            )}
                                        </td>
                                        <td className="md-td">
                                            {(() => {
                                                // For recurring trips: always show master status as primary, today's instance as secondary
                                                const todayInst = getInstanceForTrip(t._id, null); // today
                                                const filteredInst = filters.date ? getInstanceForTrip(t._id, filters.date) : null;

                                                // Recurring trips: show the status of the selected date (each date is tracked separately)
                                                const primaryStatus = t.tripFrequency === 'Recurring'
                                                    ? (filteredInst?.status || (rowTiming.state === 'ongoing' ? 'Ongoing' : 'Scheduled'))
                                                    : (t.status || 'Scheduled');

                                                const todayStatus = todayInst?.status;

                                                return (
                                                    <div>
                                                        <span className={`md-badge md-badge-${primaryStatus?.toLowerCase()}`}>{primaryStatus}</span>
                                                        {TIMING_META[rowTiming.state] && (
                                                            <div style={{ marginTop: 6, display: 'inline-flex', flexDirection: 'column', padding: '4px 8px', borderRadius: 8, background: TIMING_META[rowTiming.state].bg, color: TIMING_META[rowTiming.state].color }}>
                                                                <span style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                                                                    {rowTiming.state === 'overrunning' ? 'Overrunning' : `Not Started · ${TIMING_META[rowTiming.state].label}`}
                                                                </span>
                                                                <span style={{ fontSize: 10, fontWeight: 700 }}>
                                                                    {rowTiming.state === 'overrunning' ? 'Past end by ' : 'Late by '}{fmtLate(rowTiming.lateMins)}
                                                                </span>
                                                            </div>
                                                        )}
                                                        {rowTiming.state === 'ongoing' && (
                                                            <div style={{ fontSize: 10, color: '#2563eb', marginTop: 4, fontWeight: 700 }}>● In progress</div>
                                                        )}
                                                        {t.tripFrequency === 'Recurring' && todayStatus && !filters.date && (
                                                            <div style={{ fontSize: 10, color: todayStatus === 'Completed' ? '#10b981' : '#f59e0b', marginTop: 4, fontWeight: 700 }}>
                                                                Today: {todayStatus}
                                                            </div>
                                                        )}
                                                        {t.tripFrequency === 'Recurring' && filteredInst && (
                                                            <div style={{ fontSize: 10, color: filteredInst.status === 'Completed' ? '#10b981' : '#f59e0b', marginTop: 4, fontWeight: 700 }}>
                                                                {filters.date}: {filteredInst.status}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                        </td>
                                        <td className="md-td">
                                            <div className="md-actions">
                                                {(() => {
                                                    // For recurring: only use instance status if date filter is set; else use master status
                                                    const inst = filters.date ? getInstanceForTrip(t._id, filters.date) : null;
                                                    const status = t.tripFrequency === 'Recurring'
                                                        ? (inst?.status || t.status)
                                                        : (t.status);

                                                    if (status === 'Scheduled') {
                                                        return (
                                                            <button
                                                                onClick={() => handleUpdateInstanceStatus(t, 'Completed')}
                                                                style={{
                                                                    padding: '6px 12px', borderRadius: 8, border: 'none',
                                                                    background: '#10b981', color: 'white', fontSize: 11,
                                                                    fontWeight: 800, cursor: 'pointer', marginRight: 8
                                                                }}
                                                            >
                                                                Mark as Completed
                                                            </button>
                                                        );
                                                    }
                                                    if (status === 'Completed') {
                                                        return (
                                                            <button
                                                                onClick={() => handleUpdateInstanceStatus(t, 'Scheduled')}
                                                                style={{
                                                                    padding: '6px 12px', borderRadius: 8, border: 'none',
                                                                    background: '#00B2A9', color: 'white', fontSize: 11,
                                                                    fontWeight: 800, cursor: 'pointer', marginRight: 8
                                                                }}
                                                            >
                                                                Mark as Scheduled
                                                            </button>
                                                        );
                                                    }
                                                    return null;
                                                })()}

                                                {t.status === 'Requested' && (
                                                    <button
                                                        onClick={() => setApprovalModal(t)}
                                                        style={{
                                                            padding: '6px 12px', borderRadius: 8, border: 'none',
                                                            background: '#10b981', color: 'white', fontSize: 11,
                                                            fontWeight: 800, cursor: 'pointer', marginRight: 8
                                                        }}
                                                    >
                                                        Review & Propose Price
                                                    </button>
                                                )}
                                                {t.status === 'ClientAccepted' && (
                                                    <button
                                                        onClick={() => setModal({ data: t })}
                                                        style={{
                                                            padding: '6px 12px', borderRadius: 8, border: 'none',
                                                            background: '#00B2A9', color: 'white', fontSize: 11,
                                                            fontWeight: 800, cursor: 'pointer', marginRight: 8
                                                        }}
                                                    >
                                                        Dispatch / Assign Fleet
                                                    </button>
                                                )}
                                                <button className="md-icon-btn" onClick={() => setModal({ data: t })} title="Edit Trip"><Icon d={icons.edit} size={14} /></button>
                                                {t.tripFrequency !== 'Recurring' && (
                                                    <button 
                                                        className="md-icon-btn" 
                                                        onClick={() => setTripSheetModal({ trip: t, instance: null })} 
                                                        title="View Trip Sheet"
                                                        style={{ color: '#10b981' }}
                                                    >
                                                        <Icon d={icons.map} size={14} />
                                                    </button>
                                                )}
                                                <button 
                                                    className="md-icon-btn" 
                                                    onClick={() => onNavigate('trip-history', t._id)} 
                                                    title="View Edit History & Executions"
                                                    style={{ color: '#f59e0b' }}
                                                >
                                                    <Icon d={icons.clock} size={14} />
                                                </button>
                                                <button className="md-icon-btn md-icon-btn-danger" onClick={() => setDeleteModal(t)} title="Delete Trip"><Icon d={icons.trash} size={14} /></button>
                                            </div>
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Approval Modal */}
            {approvalModal && (
                <div className="md-modal-overlay" onClick={() => setApprovalModal(null)}>
                    <div className="md-modal" style={{ maxWidth: 500 }} onClick={e => e.stopPropagation()}>
                        <div className="md-modal-header">
                            <h3 className="md-modal-title">Approve Trip Request</h3>
                            <button className="md-modal-close" onClick={() => setApprovalModal(null)}><Icon d={icons.x} size={18} /></button>
                        </div>
                        <div className="md-modal-body">
                            <div style={{ background: '#f8fafc', padding: 12, borderRadius: 12, marginBottom: 16, border: '1px solid #e2e8f0', fontSize: 13 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, borderBottom: '1px solid #e2e8f0', paddingBottom: 6 }}>
                                    <span style={{ fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>
                                        Client: <strong style={{ color: '#063A5C', textTransform: 'none', fontSize: 13, marginLeft: 4 }}>{approvalModal.clientId?.companyName || '—'}</strong>
                                    </span>
                                    {approvalModal.tripFrequency === 'One Time' && approvalModal.tripDate && (
                                        <span style={{ fontSize: 11, fontWeight: 700, color: '#166534', background: '#f0fdf4', padding: '2px 8px', borderRadius: 6 }}>
                                            Date: {new Date(approvalModal.tripDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                        </span>
                                    )}
                                    {approvalModal.tripFrequency === 'Recurring' && (approvalModal.startDate || approvalModal.endDate) && (
                                        <span style={{ fontSize: 11, fontWeight: 700, color: '#1d4ed8', background: '#eff6ff', padding: '2px 8px', borderRadius: 6 }}>
                                            Recur: {approvalModal.startDate ? new Date(approvalModal.startDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '—'}
                                            {'➔'}
                                            {approvalModal.endDate ? new Date(approvalModal.endDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '—'}
                                        </span>
                                    )}
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, color: '#475569', marginBottom: 4 }}>
                                    <div style={{ display: 'flex', gap: 4 }}>
                                        <span style={{ fontWeight: 700, color: '#1e293b', minWidth: 55 }}>Pickup:</span>
                                        <span style={{ color: '#334155', fontSize: 12.5 }}>{approvalModal.pickupLocation}</span>
                                    </div>
                                    <div style={{ display: 'flex', gap: 4 }}>
                                        <span style={{ fontWeight: 700, color: '#1e293b', minWidth: 55 }}>Drop:</span>
                                        <span style={{ color: '#334155', fontSize: 12.5 }}>{approvalModal.dropLocation}</span>
                                    </div>
                                </div>

                                {approvalModal.requestedVehicleId && (
                                    <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <span style={{ fontSize: 11, fontWeight: 800, color: '#16539C', textTransform: 'uppercase' }}>Vehicle:</span>
                                        <span style={{ fontSize: 12, fontWeight: 700, color: '#1e293b' }}>
                                            {approvalModal.requestedVehicleId.make} {approvalModal.requestedVehicleId.model} 
                                            {approvalModal.requestedVehicleId.plateNumber ? ` (${approvalModal.requestedVehicleId.plateNumber})` : ''} 
                                            {approvalModal.requestedVehicleId.seatingCapacity ? ` • ${approvalModal.requestedVehicleId.seatingCapacity} Seats` : ''}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {!approvalModal.routeId && (
                                <div className="md-form-group" style={{ marginBottom: 20 }}>
                                    <label className="md-label">Create New Route As*</label>
                                    <input
                                        className="md-input"
                                        placeholder="Enter name for this new route (e.g. IT Park Shuttle)"
                                        onChange={(e) => approvalModal._routeName = e.target.value}
                                    />
                                    <p style={{ fontSize: 11, color: '#00B2A9', marginTop: 6, fontWeight: 600 }}>* This request proposed a new route. We'll create it automatically.</p>
                                </div>
                            )}

                            <div className="md-form-group">
                                <label className="md-label">Trip Fee / Amount ({operatorDetails?.country ? getCurrencySymbol(operatorDetails.country) : '₹'})*</label>
                                <input
                                    type="number"
                                    className="md-input"
                                    placeholder="Enter trip amount for client billing"
                                    onChange={(e) => approvalModal._amount = e.target.value}
                                    required
                                />
                            </div>
                        </div>
                        <div className="md-modal-footer">
                            <button className="md-cancel-btn" onClick={() => setApprovalModal(null)}>Ignore</button>
                            <button
                                className="md-save-btn"
                                onClick={() => handleApprove(approvalModal, {
                                    routeName: approvalModal._routeName,
                                    amount: approvalModal._amount
                                })}
                            >
                                Confirm & Propose Price
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal */}
            {modal && (
                <TripForm
                    key={modal.data?._id || 'new'}
                    data={modal.data}
                    isDispatchOnlyProp={modal.isDispatchOnly}
                    clients={clients}
                    routes={routes}
                    vehicles={vehicles}
                    drivers={drivers}
                    vendors={vendors}
                    vendorFleet={vendorFleet}
                    trips={trips}
                    mappings={mappings}
                    operatorDetails={operatorDetails}
                    onClose={() => setModal(null)} onNavigate={onNavigate}
                    onSave={handleSave}
                />
            )}

            {/* Trip Sheet Modal */}
            {tripSheetModal && (
                <TripSheetModal 
                    trip={tripSheetModal.trip} 
                    instance={tripSheetModal.instance} 
                    allocations={allocations}
                    onClose={() => setTripSheetModal(null)} 
                    operatorId={operatorId} 
                />
            )}

            {/* Delete Confirmation */}
            {deleteModal && (
                <div className="md-modal-overlay" onClick={() => setDeleteModal(null)}>
                    <div className="md-modal" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
                        <div className="md-modal-header">
                            <h3 className="md-modal-title" style={{ color: '#ef4444' }}>Confirm Delete</h3>
                        </div>
                        <div className="md-modal-body">
                            Are you sure you want to delete this trip schedule?
                        </div>
                        <div className="md-modal-footer">
                            <button className="md-cancel-btn" onClick={() => setDeleteModal(null)}>Cancel</button>
                            <button className="md-save-btn" style={{ background: '#ef4444' }} onClick={handleDelete}>Delete</button>
                        </div>
                    </div>
                </div>
            )}
            {passengerListModal && (
                <div className="md-modal-overlay" onClick={() => setPassengerListModal(null)}>
                    <div className="md-modal" style={{ maxWidth: 600 }} onClick={e => e.stopPropagation()}>
                        <div className="md-modal-header" style={{ background: '#063A5C' }}>
                            <h3 className="md-modal-title" style={{ color: 'white' }}>Manifest: Passenger List {passengerListModal.date && `(${new Date(passengerListModal.date).toLocaleDateString()})`}</h3>
                            <button className="md-modal-close" style={{ color: 'white' }} onClick={() => setPassengerListModal(null)}><Icon d={icons.x} size={18} /></button>
                        </div>
                        <div className="md-modal-body" style={{ padding: 0 }}>
                            {passengerListModal.allocations.length === 0 ? (
                                <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>No passenger data available for this trip.</div>
                            ) : (
                                <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead style={{ position: 'sticky', top: 0, background: '#f8fafc', zIndex: 1 }}>
                                            <tr>
                                                <th style={{ textAlign: 'left', padding: '12px 20px', fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0' }}>Passenger</th>
                                                <th style={{ textAlign: 'left', padding: '12px 20px', fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0' }}>Contact Details</th>
                                                <th style={{ textAlign: 'center', padding: '12px 20px', fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0' }}>Gender</th>
                                                <th style={{ textAlign: 'center', padding: '12px 20px', fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0' }}>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {passengerListModal.allocations.map((a, idx) => {
                                                const p = a.passengerId;
                                                if (!p) return null;
                                                const statusColor = a.status === 'Boarded' ? '#16a34a' : a.status === 'Absent' ? '#ef4444' : a.status === 'No Show' ? '#f59e0b' : '#64748b';
                                                const statusBg = a.status === 'Boarded' ? '#dcfce7' : a.status === 'Absent' ? '#fee2e2' : a.status === 'No Show' ? '#fef3c7' : '#f1f5f9';
                                                return (
                                                <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                    <td style={{ padding: '12px 20px' }}>
                                                        <div style={{ fontWeight: 700, color: '#063A5C', fontSize: 14 }}>{p.name}</div>
                                                        <div style={{ fontSize: 11, color: '#94a3b8' }}>ID: {p.employeeId || 'N/A'}</div>
                                                        <div style={{ fontSize: 10, color: a.tripInstanceId ? '#f59e0b' : '#3b82f6', fontWeight: 700, marginTop: 4, display: 'inline-block', padding: '2px 6px', borderRadius: 4, background: a.tripInstanceId ? '#fef3c7' : '#eff6ff' }}>
                                                            {a.tripInstanceId ? (passengerListModal.date ? `Booked for ${new Date(passengerListModal.date).toLocaleDateString()}` : 'Specific Day Booking') : 'Everyday (Panel)'}
                                                        </div>
                                                    </td>
                                                    <td style={{ padding: '12px 20px' }}>
                                                        <div style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>{p.contactNumber}</div>
                                                        <div style={{ fontSize: 11, color: '#64748b' }}>{p.email || 'No email provided'}</div>
                                                    </td>
                                                    <td style={{ padding: '12px 20px', textAlign: 'center' }}>
                                                        <span style={{
                                                            display: 'inline-block', padding: '4px 10px', borderRadius: 20, fontSize: 10, fontWeight: 800,
                                                            background: p.gender === 'Female' ? '#fdf2f8' : (p.gender === 'Male' ? '#eff6ff' : '#f1f5f9'),
                                                            color: p.gender === 'Female' ? '#db2777' : (p.gender === 'Male' ? '#2563eb' : '#64748b'),
                                                            border: `1px solid ${p.gender === 'Female' ? '#fbcfe8' : (p.gender === 'Male' ? '#bfdbfe' : '#e2e8f0')}`
                                                        }}>
                                                            {p.gender || 'Not Specified'}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: '12px 20px', textAlign: 'center' }}>
                                                        <span style={{
                                                            display: 'inline-block', padding: '4px 10px', borderRadius: 20, fontSize: 10, fontWeight: 800,
                                                            background: statusBg, color: statusColor
                                                        }}>
                                                            {a.status || 'Pending'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            )})}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                        <div className="md-modal-footer">
                            <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600 }}>Total: {passengerListModal.allocations.length} passengers</div>
                            <button className="md-cancel-btn" onClick={() => setPassengerListModal(null)}>Close Manifest</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ---- Create Trip draft (auto-saved locally, kept for 24 hours) ----
const TRIP_DRAFT_TTL = 24 * 60 * 60 * 1000;
const tripDraftKey = () => {
    try {
        const u = JSON.parse(localStorage.getItem('ops-user') || '{}');
        return `ops-trip-draft-${u.operatorId || u._id || 'default'}`;
    } catch (e) { return 'ops-trip-draft-default'; }
};
export const loadTripDraft = () => {
    try {
        const d = JSON.parse(localStorage.getItem(tripDraftKey()) || 'null');
        if (!d || !d.savedAt) return null;
        if (Date.now() - d.savedAt > TRIP_DRAFT_TTL) { localStorage.removeItem(tripDraftKey()); return null; }
        return d;
    } catch (e) { return null; }
};
const saveTripDraft = (draft) => {
    try { localStorage.setItem(tripDraftKey(), JSON.stringify({ ...draft, savedAt: Date.now() })); } catch (e) {}
};
export const clearTripDraft = () => {
    try { localStorage.removeItem(tripDraftKey()); } catch (e) {}
};
const TRIP_DRAFT_FIELDS = ['tripName', 'clientId', 'routeId', 'amount', 'pickupLocation', 'dropLocation', 'tripDate', 'startTime', 'endTime', 'vehicleId', 'driverId', 'vendorId', 'startDate', 'endDate'];
const isTripDraftEmpty = (f, pq, dq) =>
    !pq && !dq && TRIP_DRAFT_FIELDS.every(k => !f[k]) && (!f.recurringDays || f.recurringDays.length === 0);

function TripForm({ onNavigate, data, isDispatchOnlyProp, clients, routes, vehicles, drivers, vendors, vendorFleet, trips, mappings = [], operatorDetails, onClose, onSave }) {
    // Restore an unsaved "Create New Trip" draft (new trips only, kept 24h)
    const [initialDraft] = useState(() => (!data && !isDispatchOnlyProp ? loadTripDraft() : null));
    const [draftRestoredAt, setDraftRestoredAt] = useState(initialDraft?.savedAt || null);
    const [form, setForm] = useState(data ? {
        ...data,
        tripName: data.tripName || '',
        clientId: data.clientId?._id || data.clientId || '',
        routeId: data.routeId?._id || data.routeId || '',
        vehicleId: data.vehicleId?._id || data.vehicleId || '',
        driverId: data.driverId?._id || data.driverId || '',
        vendorId: data.vendorId?._id || data.vendorId || '',
        amount: data.amount || '',
        billingType: data.billingType || (data.tripType === 'on_demand' ? 'Ad-hoc' : 'Daily'),
        startDate: data.startDate || '',
        endDate: data.endDate || '',
        changeReason: ''
    } : {
        tripName: '',
        clientId: '', routeId: '',
        amount: '',
        billingType: 'Daily',
        pickupLocation: '', dropLocation: '',
        tripType: 'Login', tripFrequency: 'One Time',
        tripDate: '', recurringDays: [], status: 'Scheduled',
        startTime: '', endTime: '',
        vehicleId: '', driverId: '', vendorId: '',
        startDate: '', endDate: '',
        changeReason: '',
        ...((!isDispatchOnlyProp && initialDraft?.form) || {})
    });

    const [errors, setErrors] = useState({});
    const modalBodyRef = useRef(null);
    const [routeStops, setRouteStops] = useState([]);
    const [isVendorTrip, setIsVendorTrip] = useState(initialDraft ? !!initialDraft.isVendorTrip : !!form.vendorId);
    const [availPanel, setAvailPanel] = useState(null); // 'fleet' | 'drivers' | 'vfleet'

    const formatDateToDMY = (dateStr) => {
        if (!dateStr) return '';
        if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) return dateStr;
        if (dateStr.includes('/')) return dateStr;
        const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (match) {
            return `${match[3]}/${match[2]}/${match[1]}`;
        }
        return dateStr;
    };

    const parseDMYToYYYYMMDD = (dmyStr) => {
        if (!dmyStr) return '';
        const parts = dmyStr.split('/');
        if (parts.length === 3) {
            const dd = parseInt(parts[0], 10);
            const mm = parseInt(parts[1], 10);
            const yyyy = parseInt(parts[2], 10);
            if (isNaN(dd) || isNaN(mm) || isNaN(yyyy)) return '';
            if (mm < 1 || mm > 12) return '';
            if (dd < 1 || dd > 31) return '';
            
            const dateObj = new Date(yyyy, mm - 1, dd);
            if (dateObj.getFullYear() === yyyy && dateObj.getMonth() === mm - 1 && dateObj.getDate() === dd) {
                const ddStr = String(dd).padStart(2, '0');
                const mmStr = String(mm).padStart(2, '0');
                return `${yyyy}-${mmStr}-${ddStr}`;
            }
        }
        return '';
    };

    const formatDMYInput = (value) => {
        const clean = value.replace(/\D/g, '').slice(0, 8);
        let formatted = clean;
        if (clean.length > 4) {
            formatted = `${clean.slice(0, 2)}/${clean.slice(2, 4)}/${clean.slice(4)}`;
        } else if (clean.length > 2) {
            formatted = `${clean.slice(0, 2)}/${clean.slice(2)}`;
        }
        return formatted;
    };

    const handleDateChange = (field, value, nativeEvent) => {
        const isDeleting = nativeEvent && nativeEvent.inputType === 'deleteContentBackward';
        if (isDeleting) {
            setForm(prev => ({ ...prev, [field]: value }));
            if (errors[field]) {
                setErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
            }
            return;
        }
        const dmy = formatDMYInput(value);
        const ymd = parseDMYToYYYYMMDD(dmy);
        setForm(prev => ({ ...prev, [field]: ymd || dmy }));
        if (errors[field]) {
            setErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
        }
    };

    const BUFFER_MINUTES = 60; // 1 hour buffer between trips

    const timeToMinutes = (timeStr) => {
        if (!timeStr || typeof timeStr !== 'string') return 0;
        const [h, m] = timeStr.split(':').map(Number);
        return (h || 0) * 60 + (m || 0);
    };

    const checkAllocationConflict = (entityId, entityType, formState, tripList, ignoreTripId) => {
        if (!entityId || !formState.startTime) {
            return { hasConflict: false, conflictTrip: null, label: '', reason: '' };
        }

        const formStartMin = timeToMinutes(formState.startTime);
        let formEndMin = formState.endTime ? timeToMinutes(formState.endTime) : formStartMin + 60;
        if (formEndMin <= formStartMin) {
            formEndMin = formStartMin + 60;
        }

        const targetDate = formState.tripDate ? String(formState.tripDate).split('T')[0] : null;
        const targetStartDate = formState.startDate ? String(formState.startDate).split('T')[0] : targetDate;
        const targetEndDate = formState.endDate ? String(formState.endDate).split('T')[0] : targetDate;
        const targetDays = formState.recurringDays || [];

        const conflictTrip = (tripList || []).find(t => {
            if (t._id === (ignoreTripId || formState._id)) return false;
            if (!['Scheduled', 'Ongoing'].includes(t.status)) return false;

            const isSameEntity = entityType === 'vehicle' 
                ? (t.vehicleId?._id === entityId || t.vehicleId === entityId || t.vendorProposedVehicleId?._id === entityId || t.vendorProposedVehicleId === entityId)
                : (t.driverId?._id === entityId || t.driverId === entityId || t.vendorProposedDriverId?._id === entityId || t.vendorProposedDriverId === entityId);

            if (!isSameEntity) return false;

            const tDate = t.tripDate ? String(t.tripDate).split('T')[0] : null;
            const tStartDate = t.startDate ? String(t.startDate).split('T')[0] : tDate;
            const tEndDate = t.endDate ? String(t.endDate).split('T')[0] : tDate;
            const tDays = t.recurringDays || [];

            let isDateOverlap = false;

            if (formState.tripFrequency === 'One Time' && t.tripFrequency === 'One Time') {
                isDateOverlap = !!(targetDate && tDate && targetDate === tDate);
            } else if (formState.tripFrequency === 'Recurring' && t.tripFrequency === 'Recurring') {
                const hasCommonDay = targetDays.some(d => tDays.includes(d));
                const hasDateRangeOverlap = (!targetStartDate || !tEndDate || targetStartDate <= tEndDate) &&
                                            (!targetEndDate || !tStartDate || targetEndDate >= tStartDate);
                isDateOverlap = hasCommonDay && hasDateRangeOverlap;
            } else if (formState.tripFrequency === 'One Time' && t.tripFrequency === 'Recurring') {
                if (targetDate) {
                    const dayName = new Date(targetDate).toLocaleDateString('en-US', { weekday: 'short' });
                    const isInRange = (!tStartDate || targetDate >= tStartDate) && (!tEndDate || targetDate <= tEndDate);
                    isDateOverlap = isInRange && tDays.includes(dayName);
                }
            } else if (formState.tripFrequency === 'Recurring' && t.tripFrequency === 'One Time') {
                if (tDate) {
                    const dayName = new Date(tDate).toLocaleDateString('en-US', { weekday: 'short' });
                    const isInRange = (!targetStartDate || tDate >= targetStartDate) && (!targetEndDate || tDate <= targetEndDate);
                    isDateOverlap = isInRange && targetDays.includes(dayName);
                }
            } else {
                if (targetDate && tDate) {
                    isDateOverlap = targetDate === tDate;
                }
            }

            if (!isDateOverlap) return false;

            if (t.billingType === 'Monthly' || formState.billingType === 'Monthly') {
                return true;
            }

            if (!t.startTime) return false;
            const tStartMin = timeToMinutes(t.startTime);
            let tEndMin = t.endTime ? timeToMinutes(t.endTime) : tStartMin + 60;
            if (tEndMin <= tStartMin) {
                tEndMin = tStartMin + 60;
            }

            return (formStartMin < (tEndMin + BUFFER_MINUTES)) && (tStartMin < (formEndMin + BUFFER_MINUTES));
        });

        if (conflictTrip) {
            const tripName = conflictTrip.tripName || conflictTrip.tripNumber || (conflictTrip.routeId?.routeName || `${conflictTrip.pickupLocation || ''} ➔ ${conflictTrip.dropLocation || ''}`).slice(0, 24) || 'Another trip';
            const tripTiming = `${conflictTrip.startTime || '—'} - ${conflictTrip.endTime || conflictTrip.startTime || '—'}`;
            const reason = `Already assigned to ${tripName} (${tripTiming}) with 1h buffer.`;
            const label = `⚠️ (Busy: ${tripTiming} + 1h buffer - Not Allowed)`;
            return { hasConflict: true, conflictTrip, label, reason };
        }

        return { hasConflict: false, conflictTrip: null, label: '', reason: '' };
    };

    const getDriverCompliance = (driver, targetDate) => {
        if (!driver) return { isCompliant: true, expiredDocs: [], label: '' };
        const dateToCheck = targetDate ? String(targetDate).split('T')[0] : new Date().toISOString().split('T')[0];
        const expiredDocs = [];

        if (driver.licenseExpiry && String(driver.licenseExpiry).split('T')[0] < dateToCheck) {
            expiredDocs.push('License');
        }
        if (driver.civilIdExpiry && String(driver.civilIdExpiry).split('T')[0] < dateToCheck) {
            expiredDocs.push('Civil ID');
        }
        if (driver.passportExpiry && String(driver.passportExpiry).split('T')[0] < dateToCheck) {
            expiredDocs.push('Passport');
        }

        const isInactive = driver.status && ['Inactive', 'Terminated', 'Resigned'].includes(driver.status);
        if (isInactive) {
            expiredDocs.push(`Status: ${driver.status}`);
        }

        const isCompliant = expiredDocs.length === 0;
        const label = !isCompliant ? `⚠️ (${expiredDocs.join(', ')} Expired - Not Allowed)` : '';
        return { isCompliant, expiredDocs, label };
    };

    const getVehicleCompliance = (vehicle, targetDate) => {
        if (!vehicle) return { isCompliant: true, expiredDocs: [], label: '' };
        const dateToCheck = targetDate ? String(targetDate).split('T')[0] : new Date().toISOString().split('T')[0];
        const expiredDocs = [];

        if (vehicle.vehicleLicenseExpiry && String(vehicle.vehicleLicenseExpiry).split('T')[0] < dateToCheck) {
            expiredDocs.push('Permit');
        }
        if (vehicle.registrationExpiry && String(vehicle.registrationExpiry).split('T')[0] < dateToCheck) {
            expiredDocs.push('Registration');
        }
        if (vehicle.insuranceExpiry && String(vehicle.insuranceExpiry).split('T')[0] < dateToCheck) {
            expiredDocs.push('Insurance');
        }

        const isInactive = (vehicle.vehicleStatus || vehicle.status) && ['Under Maintenance', 'Damaged', 'Retired', 'Inactive'].includes(vehicle.vehicleStatus || vehicle.status);
        if (isInactive) {
            expiredDocs.push(vehicle.vehicleStatus || vehicle.status);
        }

        const isCompliant = expiredDocs.length === 0;
        const label = !isCompliant ? `⚠️ (${expiredDocs.join(', ')} Expired - Not Allowed)` : '';
        return { isCompliant, expiredDocs, label };
    };

    const handleConfirm = () => {
        const newErrors = {};
        const formToSave = { ...form };

        if (!isDispatchOnly) {
            if (!form.clientId) {
                newErrors.clientId = 'Client field is required. Please select a client.';
            }
            if (!form.routeId) {
                newErrors.routeId = 'Route selection field is empty. Please select a route.';
            }
            if (!form.pickupLocation || !form.pickupLocation.trim()) {
                newErrors.pickupLocation = 'Pickup location is required. Please select a route or search for a pickup point.';
            }
            if (!form.dropLocation || !form.dropLocation.trim()) {
                newErrors.dropLocation = 'Drop location is required. Please select a route or search for a destination.';
            }

            if (form.tripFrequency === 'One Time') {
                if (!form.tripDate) {
                    newErrors.tripDate = 'Trip Date is required. Please enter a date.';
                } else {
                    formToSave.tripDate = String(form.tripDate).split('T')[0];
                    if (!/^\d{4}-\d{2}-\d{2}$/.test(formToSave.tripDate)) {
                        newErrors.tripDate = 'Please enter a valid Trip Date in DD/MM/YYYY format.';
                    }
                }
            } else {
                if (!form.startDate) {
                    newErrors.startDate = 'Effective From date is required.';
                } else {
                    formToSave.startDate = String(form.startDate).split('T')[0];
                    if (!/^\d{4}-\d{2}-\d{2}$/.test(formToSave.startDate)) {
                        newErrors.startDate = 'Please enter a valid Effective From date in DD/MM/YYYY format.';
                    }
                }
                if (form.endDate) {
                    formToSave.endDate = String(form.endDate).split('T')[0];
                    if (!/^\d{4}-\d{2}-\d{2}$/.test(formToSave.endDate)) {
                        newErrors.endDate = 'Please enter a valid Effective To date in DD/MM/YYYY format.';
                    }
                } else if (!formToSave.endDate && formToSave.startDate) {
                    const sDate = new Date(formToSave.startDate);
                    const eDate = new Date(sDate.getFullYear(), sDate.getMonth() + 1, 0);
                    formToSave.endDate = eDate.getFullYear() + '-' + String(eDate.getMonth() + 1).padStart(2, '0') + '-' + String(eDate.getDate()).padStart(2, '0');
                }
            }

            if (!form.startTime) {
                newErrors.startTime = 'Start Time is required.';
            }
            if (!form.endTime) {
                newErrors.endTime = 'End Time is required.';
            }

            if (isVendorTrip && !form.vendorId) {
                newErrors.vendorId = 'Please select a vendor.';
            }
        }

        // CT-08: only skip the clash check when editing a trip whose assignment AND schedule are unchanged
        const dk = (v) => (v ? String(v).split('T')[0] : '');
        const scheduleUnchanged = !!data?._id &&
            (data.startTime || '') === (form.startTime || '') &&
            (data.endTime || '') === (form.endTime || '') &&
            dk(data.tripDate) === dk(form.tripDate) &&
            dk(data.startDate) === dk(form.startDate) &&
            dk(data.endDate) === dk(form.endDate) &&
            (data.tripFrequency || '') === (form.tripFrequency || '') &&
            [...(data.recurringDays || [])].sort().join(',') === [...(form.recurringDays || [])].sort().join(',');
        const keepsSame = (field) => scheduleUnchanged && (data?.[field]?._id || data?.[field]) === form[field];

        if (form.vehicleId) {
            const vehObj = isVendorTrip 
                ? vendorFleet.find(f => f._id === form.vehicleId) 
                : vehicles.find(v => v._id === form.vehicleId);
            const vehComp = getVehicleCompliance(vehObj, form.tripDate || form.startDate);
            if (!vehComp.isCompliant && (data?.vehicleId?._id || data?.vehicleId) !== form.vehicleId) {
                newErrors.vehicleId = `Selected vehicle has expired documents (${vehComp.expiredDocs.join(', ')}). Please select a compliant vehicle.`;
            } else {
                const vehConflict = checkAllocationConflict(form.vehicleId, 'vehicle', form, trips, data?._id);
                if (vehConflict.hasConflict && !keepsSame('vehicleId')) {
                    newErrors.vehicleId = `Vehicle conflict: ${vehConflict.reason}`;
                }
            }
        }

        if (form.driverId) {
            const drvObj = drivers.find(d => d._id === form.driverId);
            const drvComp = getDriverCompliance(drvObj, form.tripDate || form.startDate);
            if (!drvComp.isCompliant && (data?.driverId?._id || data?.driverId) !== form.driverId) {
                newErrors.driverId = `Selected driver has expired documents (${drvComp.expiredDocs.join(', ')}). Cannot assign this driver to the trip.`;
            } else {
                const drvConflict = checkAllocationConflict(form.driverId, 'driver', form, trips, data?._id);
                if (drvConflict.hasConflict && !keepsSame('driverId')) {
                    newErrors.driverId = `Driver conflict: ${drvConflict.reason}`;
                }
            }
        }

        if (form.vehicleId && form.driverId && !isVendorTrip) {
            const drvVeh = getDriverMappedVehicleId(form.driverId);
            const origDrv = data?.driverId?._id || data?.driverId;
            if (drvVeh && drvVeh !== form.vehicleId && form.driverId !== origDrv) {
                newErrors.driverId = `This driver is mapped to ${vehicleLabel(drvVeh)}. Change the mapping first, or pick an unmapped driver.`;
            }
        }
        if (driverLocked && form.driverId !== vehicleMapping.driverId) {
            newErrors.driverId = `This vehicle is mapped to ${vehicleMapping.name}. Only the mapped driver can be assigned.`;
        }

        if (data && !isDispatchOnly && data.vehicleId && !form.changeReason) {
            newErrors.changeReason = 'Please provide a reason for updating this trip schedule.';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            if (modalBodyRef.current) {
                modalBodyRef.current.scrollTo({ top: 0, behavior: 'smooth' });
            }
            return;
        }

        setErrors({});
        onSave(formToSave);
    };

    const mapRef = useRef(null);
    const tripDateRef = useRef(null);
    const startDateRef = useRef(null);
    const endDateRef = useRef(null);
    const mapInstance = useRef(null);
    const routeLayer = useRef(null);
    const pickupMarker = useRef(null);
    const dropMarker = useRef(null);

    const [pickupQuery, setPickupQuery] = useState(initialDraft?.pickupQuery || '');
    const [dropQuery, setDropQuery] = useState(initialDraft?.dropQuery || '');

    // Auto-save the Create Trip form as a draft while typing (new trips only)
    useEffect(() => {
        if (data || isDispatchOnlyProp) return;
        const t = setTimeout(() => {
            if (isTripDraftEmpty(form, pickupQuery, dropQuery)) clearTripDraft();
            else saveTripDraft({ form: { ...form, changeReason: '' }, isVendorTrip, pickupQuery, dropQuery });
        }, 300);
        return () => clearTimeout(t);
    }, [form, isVendorTrip, pickupQuery, dropQuery, data, isDispatchOnlyProp]);

    const handleClearDraft = () => {
        clearTripDraft();
        onClose();
    };
    const [pickupSuggestions, setPickupSuggestions] = useState([]);
    const [dropSuggestions, setDropSuggestions] = useState([]);
    const [showPickupSuggestions, setShowPickupSuggestions] = useState(false);
    const [showDropSuggestions, setShowDropSuggestions] = useState(false);

    // Initialize Map
    useEffect(() => {
        if (!mapInstance.current && mapRef.current) {
            mapInstance.current = L.map(mapRef.current).setView([20.5937, 78.9629], 5);
            L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                maxZoom: 19,
                attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            }).addTo(mapInstance.current);

            delete L.Icon.Default.prototype._getIconUrl;
            L.Icon.Default.mergeOptions({
                iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
                iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
                shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
            });
        }
    }, []);

    const searchLocation = async (query) => {
        if (!query || query.length < 3) return [];
        try {
            const res = await axios.get(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5&accept-language=en`);
            return res.data;
        } catch (error) { return []; }
    };

    useEffect(() => {
        const delayDebounceFn = setTimeout(async () => {
            if (pickupQuery && pickupQuery !== form.pickupLocation) {
                const results = await searchLocation(pickupQuery);
                setPickupSuggestions(results);
                setShowPickupSuggestions(true);
            } else {
                setPickupSuggestions([]);
            }
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [pickupQuery, form.pickupLocation]);

    useEffect(() => {
        const delayDebounceFn = setTimeout(async () => {
            if (dropQuery && dropQuery !== form.dropLocation) {
                const results = await searchLocation(dropQuery);
                setDropSuggestions(results);
                setShowDropSuggestions(true);
            } else {
                setDropSuggestions([]);
            }
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [dropQuery, form.dropLocation]);

    const handleSelectLocation = (place, type) => {
        const lat = parseFloat(place.lat);
        const lon = parseFloat(place.lon);
        const displayName = place.display_name;

        if (type === 'pickup') {
            setForm(prev => ({ ...prev, pickupLocation: displayName, pickupLat: lat, pickupLng: lon }));
            setPickupQuery(displayName);
            setShowPickupSuggestions(false);
            if (pickupMarker.current) mapInstance.current.removeLayer(pickupMarker.current);
            pickupMarker.current = L.marker([lat, lon], { title: 'Pickup' }).addTo(mapInstance.current);
        } else {
            setForm(prev => ({ ...prev, dropLocation: displayName, dropLat: lat, dropLng: lon }));
            setDropQuery(displayName);
            setShowDropSuggestions(false);
            if (dropMarker.current) mapInstance.current.removeLayer(dropMarker.current);
            dropMarker.current = L.marker([lat, lon], { title: 'Drop' }).addTo(mapInstance.current);
        }

        if (mapInstance.current) {
            mapInstance.current.setView([lat, lon], 12);
        }
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (!e.target.closest('.pickup-search-container')) setShowPickupSuggestions(false);
            if (!e.target.closest('.drop-search-container')) setShowDropSuggestions(false);
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    useEffect(() => {
        if (data && data.pickupLocation) setPickupQuery(data.pickupLocation);
        if (data && data.dropLocation) setDropQuery(data.dropLocation);
    }, [data]);

    useEffect(() => {
        const calculateRoute = async () => {
            if (!mapInstance.current) return;

            if (form.pickupLat && form.pickupLng) {
                if (pickupMarker.current) mapInstance.current.removeLayer(pickupMarker.current);
                pickupMarker.current = L.marker([form.pickupLat, form.pickupLng], { title: 'Pickup' }).addTo(mapInstance.current);
            }
            if (form.dropLat && form.dropLng) {
                if (dropMarker.current) mapInstance.current.removeLayer(dropMarker.current);
                dropMarker.current = L.marker([form.dropLat, form.dropLng], { title: 'Drop' }).addTo(mapInstance.current);
            }

            if (!form.pickupLat || !form.pickupLng || !form.dropLat || !form.dropLng) {
                if (form.pickupLat && form.pickupLng) {
                    mapInstance.current.setView([form.pickupLat, form.pickupLng], 13);
                }
                return;
            }

            try {
                const url = `https://router.project-osrm.org/route/v1/driving/${form.pickupLng},${form.pickupLat};${form.dropLng},${form.dropLat}?overview=full&geometries=geojson`;
                const response = await axios.get(url);
                
                if (response.data && response.data.routes && response.data.routes.length > 0) {
                    const route = response.data.routes[0];
                    if (routeLayer.current) mapInstance.current.removeLayer(routeLayer.current);
                    const geojsonRoute = { type: "Feature", properties: {}, geometry: route.geometry };
                    routeLayer.current = L.geoJSON(geojsonRoute, {
                        style: { color: '#16539C', weight: 5, opacity: 0.7 }
                    }).addTo(mapInstance.current);
                    mapInstance.current.fitBounds(routeLayer.current.getBounds(), { padding: [50, 50] });
                }
            } catch (error) { console.error("OSRM error:", error); }
        };
        const timer = setTimeout(calculateRoute, 400); 
        return () => clearTimeout(timer);
    }, [form.pickupLat, form.pickupLng, form.dropLat, form.dropLng]);

    const fetchRouteStops = async (routeId) => {
        try {
            const res = await axios.get(`${API}/route-stops/${routeId}`);
            setRouteStops(res.data);
            showStopsOnMap(res.data);
        } catch (err) { console.error(err); }
    };

    const showStopsOnMap = (stops) => {
        if (!mapInstance.current) return;
        if (stops.length > 0) {
            mapInstance.current.setView([stops[0].lat, stops[0].lng], 13);
        }
        stops.forEach(stop => {
            L.circleMarker([stop.lat, stop.lng], {
                radius: 6,
                fillColor: "#fff",
                fillOpacity: 1,
                color: "#00B2A9",
                weight: 2,
            }).bindTooltip(stop.stopName || stop.name).addTo(mapInstance.current);
        });
    };

    useEffect(() => {
        if (form.routeId) fetchRouteStops(form.routeId);
    }, []);

    const toggleDay = (day) => {
        const days = form.recurringDays.includes(day)
            ? form.recurringDays.filter(d => d !== day)
            : [...form.recurringDays, day];
        setForm({ ...form, recurringDays: days });
    };

    const isDispatchOnly = isDispatchOnlyProp || form.status === 'ClientAccepted';

    // ---- Driver ↔ Vehicle mapping lock ----
    // If the selected vehicle has an active mapping and that driver is available,
    // the trip must use that driver (other drivers can't be picked).
    const isDriverOnLeave = (d) => {
        if (!d) return false;
        if (d.status === 'On Leave') return true;
        if (!d.leaves || !d.leaves.length) return false;
        const targetTime = (form.tripDate || form.startDate) ? new Date(String(form.tripDate || form.startDate).split('T')[0]).getTime() : new Date().setHours(0, 0, 0, 0);
        return d.leaves.some(l => {
            if (l.status !== 'Approved') return false;
            const st = new Date(l.startDate).setHours(0, 0, 0, 0);
            const en = new Date(l.endDate).setHours(23, 59, 59, 999);
            return targetTime >= st && targetTime <= en;
        });
    };
    const getVehicleMappingInfo = (vehicleId) => {
        if (!vehicleId || isVendorTrip || !Array.isArray(mappings)) return { status: 'none' };
        const m = mappings.find(x => x.isActive !== false && (x.vehicleId?._id || x.vehicleId) === vehicleId);
        if (!m) return { status: 'none' };
        const driverId = m.driverId?._id || m.driverId;
        const driver = drivers.find(d => d._id === driverId) || (typeof m.driverId === 'object' ? m.driverId : null);
        const name = driver?.name || 'Mapped driver';
        if (!drivers.find(d => d._id === driverId)) return { status: 'unavailable', driverId, name, reason: 'not in driver list' };
        const comp = getDriverCompliance(driver, form.tripDate || form.startDate);
        if (!comp.isCompliant) return { status: 'unavailable', driverId, name, reason: comp.expiredDocs.join(', ') };
        if (isDriverOnLeave(driver)) return { status: 'unavailable', driverId, name, reason: 'on leave' };
        const conflict = checkAllocationConflict(driverId, 'driver', form, trips, data?._id);
        if (conflict.hasConflict) return { status: 'unavailable', driverId, name, reason: 'busy at this time' };
        return { status: 'locked', driverId, name, code: driver?.driverCode };
    };
    // Vehicle a driver is actively mapped to (or null)
    const getDriverMappedVehicleId = (driverId) => {
        if (!driverId || !Array.isArray(mappings)) return null;
        const m = mappings.find(x => x.isActive !== false && (x.driverId?._id || x.driverId) === driverId);
        return m ? (m.vehicleId?._id || m.vehicleId) : null;
    };
    const vehicleLabel = (vid) => {
        const v = vehicles.find(x => x._id === vid);
        return v ? (v.vehicleCode || v.registrationNumber || v.plateNumber) : 'another vehicle';
    };
    const vehicleMapping = getVehicleMappingInfo(form.vehicleId);
    const originalDriverId = data?.driverId?._id || data?.driverId;
    // When editing, keep an already-saved different driver allowed
    const driverLocked = vehicleMapping.status === 'locked' && !(data && originalDriverId && form.driverId === originalDriverId && originalDriverId !== vehicleMapping.driverId);

    // Keep the mapped driver selected whenever the lock applies
    useEffect(() => {
        if (driverLocked && form.driverId !== vehicleMapping.driverId) {
            setForm(prev => ({ ...prev, driverId: vehicleMapping.driverId }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [driverLocked, vehicleMapping.driverId]);

    return (
        <>
            {availPanel && (
                <AvailabilityPanel
                    onClose={() => setAvailPanel(null)}
                    vehicles={vehicles}
                    drivers={drivers}
                    vendorFleet={vendorFleet}
                    trips={trips || []}
                />
            )}
            <div className="md-modal-overlay" onClick={onClose}>
                <div className="md-modal" style={{ maxWidth: isDispatchOnly ? 550 : 1100, width: '95%' }} onClick={e => e.stopPropagation()}>
                    <div className="md-modal-header">
                        <h3 className="md-modal-title">{isDispatchOnly ? 'Dispatch / Assign Fleet' : (data ? 'Edit Trip Schedule' : 'Create New Trip')}</h3>
                        <button className="md-modal-close" onClick={onClose}><Icon d={icons.x} size={18} /></button>
                    </div>
                    <div ref={modalBodyRef} className="md-modal-body" style={{ display: isDispatchOnly ? 'block' : 'grid', gridTemplateColumns: isDispatchOnly ? 'none' : '1fr 1.25fr', gap: '30px', padding: 25, maxHeight: '82vh', overflowY: 'auto' }}>
                        <div className="form-fields" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                            {draftRestoredAt && !data && !isDispatchOnly && (
                                <div style={{ gridColumn: 'span 2', padding: '10px 14px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, fontSize: 13, color: '#1e3a8a' }}>
                                    <span><strong>Draft restored</strong> — last saved {new Date(draftRestoredAt).toLocaleString([], { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                                    <button type="button" onClick={() => {
                                        clearTripDraft();
                                        setDraftRestoredAt(null);
                                        setForm({ tripName: '', clientId: '', routeId: '', amount: '', billingType: 'Daily', pickupLocation: '', dropLocation: '', tripType: 'Login', tripFrequency: 'One Time', tripDate: '', recurringDays: [], status: 'Scheduled', startTime: '', endTime: '', vehicleId: '', driverId: '', vendorId: '', startDate: '', endDate: '', changeReason: '' });
                                        setIsVendorTrip(false);
                                        setPickupQuery('');
                                        setDropQuery('');
                                        setErrors({});
                                    }} style={{ background: 'white', border: '1px solid #bfdbfe', color: '#1d4ed8', borderRadius: 8, padding: '5px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Start fresh</button>
                                </div>
                            )}
                            {Object.keys(errors).length > 0 && (
                                <div style={{
                                    gridColumn: 'span 2',
                                    padding: '12px 16px',
                                    background: '#fef2f2',
                                    border: '1.5px solid #fca5a5',
                                    borderRadius: 12,
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 12,
                                    marginBottom: 5
                                }}>
                                    <div style={{ fontSize: 18, lineHeight: 1 }}>⚠️</div>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: 13, fontWeight: 700, color: '#991b1b', marginBottom: 2 }}>
                                            Please fill in all required fields
                                        </div>
                                        <div style={{ fontSize: 12, color: '#b91c1c' }}>
                                            {Object.values(errors)[0]} {Object.keys(errors).length > 1 ? `(+${Object.keys(errors).length - 1} more field${Object.keys(errors).length > 2 ? 's' : ''} to check)` : ''}
                                        </div>
                                    </div>
                                </div>
                            )}
                            {!isDispatchOnly && (
                                <>
                                    <div className="md-form-group" style={{ gridColumn: 'span 2' }}>
                                        <label className="md-label">Trip Name (Optional)</label>
                                        <input 
                                            className="md-input" 
                                            placeholder="e.g. Morning Shift Shuttle" 
                                            value={form.tripName} 
                                            onChange={e => setForm({ ...form, tripName: e.target.value })} 
                                        />
                                    </div>
                                    {data?.requestedVehicleId && (
                                        <div style={{ gridColumn: 'span 2', background: '#eef2ff', padding: 12, borderRadius: 12, border: '1px solid #c3dcf5', display: 'flex', alignItems: 'center', gap: 12 }}>
                                            <div style={{ width: 36, height: 36, borderRadius: 8, background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <Icon d={icons.truck} size={18} color="#16539C" />
                                            </div>
                                            <div>
                                                <div style={{ fontSize: 11, fontWeight: 800, color: '#16539C', textTransform: 'uppercase' }}>Client Requested Vehicle</div>
                                                <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>
                                                    {data.requestedVehicleId.make} {data.requestedVehicleId.model} ({data.requestedVehicleId.seatingCapacity} Seats)
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    <div className="md-form-group">
                                        <label className="md-label" style={{ color: errors.clientId ? '#dc2626' : undefined }}>Client*</label>
                                        <select 
                                            className="md-input" 
                                            style={{
                                                borderColor: errors.clientId ? '#ef4444' : undefined,
                                                backgroundColor: errors.clientId ? '#fef2f2' : undefined,
                                                boxShadow: errors.clientId ? '0 0 0 3px rgba(239, 68, 68, 0.15)' : undefined
                                            }}
                                            value={form.clientId?._id || form.clientId} 
                                            onChange={e => {
                                                const val = e.target.value;
                                                setForm({ ...form, clientId: val });
                                                if (errors.clientId) {
                                                    setErrors(prev => { const n = { ...prev }; delete n.clientId; return n; });
                                                }
                                            }}
                                        >
                                            <option value="">Select Client</option>
                                            {clients.map(c => <option key={c._id} value={c._id}>{c.companyName}</option>)}
                                        </select>
                                        {errors.clientId && (
                                            <div style={{ color: '#ef4444', fontSize: '11px', fontWeight: 600, marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <span>⚠️</span> {errors.clientId}
                                            </div>
                                        )}
                                    </div>
                                    <div className="md-form-group">
                                        <label className="md-label">Trip Type*</label>
                                        <select 
                                            className="md-input" 
                                            value={form.tripType} 
                                            onChange={e => {
                                                const newType = e.target.value;
                                                const r = routes.find(x => x._id === form.routeId);
                                                if (r) {
                                                    const isLogout = newType === 'Logout';
                                                    const nextP = isLogout ? (r.dropLocation || '') : (r.pickupLocation || '');
                                                    const nextD = isLogout ? (r.pickupLocation || '') : (r.dropLocation || '');
                                                    setPickupQuery(nextP);
                                                    setDropQuery(nextD);
                                                    setForm({
                                                        ...form,
                                                        tripType: newType,
                                                        pickupLocation: nextP,
                                                        dropLocation: nextD,
                                                        pickupLat: isLogout ? r.dropLat : r.pickupLat,
                                                        pickupLng: isLogout ? r.dropLng : r.pickupLng,
                                                        dropLat: isLogout ? r.pickupLat : r.dropLat,
                                                        dropLng: isLogout ? r.pickupLng : r.dropLng,
                                                    });
                                                } else {
                                                    setForm({ ...form, tripType: newType });
                                                }
                                            }}
                                        >
                                            <option value="Login">Login (Pickup)</option>
                                            <option value="Logout">Logout (Drop)</option>
                                            {form.tripType === 'on_demand' && <option value="on_demand">Adhoc Trip</option>}
                                        </select>
                                    </div>
                                    <div className="md-form-group" style={{ gridColumn: 'span 2' }}>
                                        <label className="md-label">Billing Category*</label>
                                        <select className="md-input" value={form.billingType} onChange={e => setForm({ ...form, billingType: e.target.value })} required>
                                            <option value="Daily">Daily</option>
                                            <option value="Monthly">Monthly</option>
                                            <option value="Shuttle">Shuttle</option>
                                            <option value="Shuttle with Booking">Shuttle with Booking</option>
                                            <option value="Ad-hoc">Ad-hoc</option>
                                        </select>
                                    </div>
                                    <div className="md-form-group" style={{ gridColumn: 'span 2' }}>
                                        <label className="md-label" style={{ color: errors.routeId ? '#dc2626' : undefined }}>Route Selection*</label>
                                        <select
                                            className="md-input"
                                            style={{
                                                borderColor: errors.routeId ? '#ef4444' : undefined,
                                                backgroundColor: errors.routeId ? '#fef2f2' : undefined,
                                                boxShadow: errors.routeId ? '0 0 0 3px rgba(239, 68, 68, 0.15)' : undefined
                                            }}
                                            value={form.routeId}
                                            onChange={e => {
                                                const rid = e.target.value;
                                                const r = routes.find(x => x._id === rid);
                                                const isLogout = form.tripType === 'Logout';
                                                const nextP = isLogout ? (r?.dropLocation || '') : (r?.pickupLocation || '');
                                                const nextD = isLogout ? (r?.pickupLocation || '') : (r?.dropLocation || '');
                                                setPickupQuery(nextP);
                                                setDropQuery(nextD);
                                                setForm({
                                                    ...form,
                                                    routeId: rid,
                                                    pickupLocation: nextP,
                                                    dropLocation: nextD,
                                                    pickupLat: isLogout ? r?.dropLat : r?.pickupLat,
                                                    pickupLng: isLogout ? r?.dropLng : r?.pickupLng,
                                                    dropLat: isLogout ? r?.pickupLat : r?.dropLat,
                                                    dropLng: isLogout ? r?.pickupLng : r?.dropLng,
                                                });
                                                if (errors.routeId || errors.pickupLocation || errors.dropLocation) {
                                                    setErrors(prev => {
                                                        const n = { ...prev };
                                                        if (rid) delete n.routeId;
                                                        if (nextP) delete n.pickupLocation;
                                                        if (nextD) delete n.dropLocation;
                                                        return n;
                                                    });
                                                }
                                                if (rid) fetchRouteStops(rid);
                                            }}
                                        >
                                            <option value="">Link to an existing route...</option>
                                            {routes
                                                .filter(r => !form.clientId || (r.clientId?._id || r.clientId) === (form.clientId?._id || form.clientId))
                                                .map(r => <option key={r._id} value={r._id}>{r.name} ({r.distance}km)</option>)}
                                        </select>
                                        {errors.routeId && (
                                            <div style={{ color: '#ef4444', fontSize: '11px', fontWeight: 600, marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <span>⚠️</span> {errors.routeId}
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}

                            {isDispatchOnly && (
                                <div style={{ gridColumn: 'span 2', padding: '15px', background: '#eff6ff', borderRadius: 12, border: '1px solid #bfdbfe', marginBottom: 5 }}>
                                    <div style={{ fontSize: 13, fontWeight: 800, color: '#1e40af', marginBottom: 8 }}>Trip Details Summary</div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 20px' }}>
                                        <div>
                                            <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>Client</div>
                                            <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>{clients.find(c => (c._id === (form.clientId?._id || form.clientId)))?.companyName || '—'}</div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>Trip Type</div>
                                            <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>{form.tripType} · {form.billingType}</div>
                                        </div>
                                        <div style={{ gridColumn: 'span 2' }}>
                                            <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>Route</div>
                                            <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>{form.pickupLocation} ➔ {form.dropLocation}</div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>Date & Time</div>
                                            <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>
                                                {form.tripDate ? new Date(form.tripDate).toLocaleDateString() : '—'} @ {form.startTime}
                                            </div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>Final Amount</div>
                                            <div style={{ fontSize: 14, fontWeight: 900, color: '#059669' }}>{formatCurrency(form.amount, operatorDetails?.country)}</div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="md-form-group" style={{ gridColumn: 'span 2' }}>
                                <div style={{ display: 'flex', gap: 15, alignItems: 'center', marginBottom: 8, padding: '10px 12px', background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0' }}>
                                    <label className="md-label" style={{ margin: 0, fontSize: 13, userSelect: 'none', cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            style={{ marginRight: 8 }}
                                            checked={isVendorTrip}
                                            onChange={e => {
                                                setIsVendorTrip(e.target.checked);
                                                if (!e.target.checked) setForm(prev => ({ ...prev, vendorId: '', vehicleId: '', driverId: '' }));
                                            }}
                                        />
                                        Assign to Vendor?
                                    </label>
                                </div>

                                {isVendorTrip ? (
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginTop: 10 }}>
                                        <div className="md-form-group">
                                            <label className="md-label" style={{ color: errors.vendorId ? '#dc2626' : undefined }}>Vendor</label>
                                            <select 
                                                className="md-input" 
                                                style={{
                                                    borderColor: errors.vendorId ? '#ef4444' : undefined,
                                                    backgroundColor: errors.vendorId ? '#fef2f2' : undefined,
                                                    boxShadow: errors.vendorId ? '0 0 0 3px rgba(239, 68, 68, 0.15)' : undefined
                                                }}
                                                value={form.vendorId} 
                                                onChange={e => {
                                                    const val = e.target.value;
                                                    setForm({ ...form, vendorId: val, vehicleId: '', driverId: '' });
                                                    if (errors.vendorId) {
                                                        setErrors(prev => { const n = { ...prev }; delete n.vendorId; return n; });
                                                    }
                                                }}
                                            >
                                                <option value="">Select Vendor</option>
                                                {vendors.map(v => <option key={v._id} value={v._id}>{v.vendorName}</option>)}
                                            </select>
                                            {errors.vendorId && (
                                                <div style={{ color: '#ef4444', fontSize: '11px', fontWeight: 600, marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <span>⚠️</span> {errors.vendorId}
                                                </div>
                                            )}
                                        </div>
                                        <div className="md-form-group">
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                                                <label className="md-label" style={{ margin: 0 }}>Vendor Vehicle</label>
                                                <button type="button" onClick={() => setAvailPanel('vfleet')} title="Check vendor fleet availability" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 7, border: '1px solid #c3dcf5', background: '#eef2ff', color: '#16539C', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                                                    <Icon d={icons.calendar} size={11} strokeWidth={2} /> Availability
                                                </button>
                                            </div>
                                            <select className="md-input" style={{
                                                borderColor: (errors.vehicleId || checkAllocationConflict(form.vehicleId, 'vehicle', form, trips, data?._id).hasConflict) ? '#ef4444' : '#e2e8f0',
                                                backgroundColor: (errors.vehicleId || checkAllocationConflict(form.vehicleId, 'vehicle', form, trips, data?._id).hasConflict) ? '#fef2f2' : undefined,
                                                boxShadow: (errors.vehicleId || checkAllocationConflict(form.vehicleId, 'vehicle', form, trips, data?._id).hasConflict) ? '0 0 0 3px rgba(239, 68, 68, 0.15)' : undefined
                                            }} value={form.vehicleId} onChange={e => {
                                                const val = e.target.value;
                                                setForm({ ...form, vehicleId: val });
                                                if (errors.vehicleId) {
                                                    setErrors(prev => { const n = { ...prev }; delete n.vehicleId; return n; });
                                                }
                                            }}>
                                                <option value="">Select Vehicle</option>
                                                {vendorFleet.filter(f => f.vendorId?._id === form.vendorId || f.vendorId === form.vendorId).map(f => {
                                                    const comp = getVehicleCompliance(f, form.tripDate || form.startDate);
                                                    const conflict = checkAllocationConflict(f._id, 'vehicle', form, trips, data?._id);
                                                    const isCurrent = (data?.vehicleId?._id || data?.vehicleId) === f._id;
                                                    const isDisabled = (!comp.isCompliant || conflict.hasConflict) && !isCurrent;
                                                    const badge = !comp.isCompliant ? comp.label : (conflict.hasConflict ? conflict.label : '');
                                                    return (
                                                        <option key={f._id} value={f._id} disabled={isDisabled} style={isDisabled ? { color: '#94a3b8' } : {}}>
                                                            {f.registrationNumber || f.plateNumber} ({f.vehicleType}) {f.vehicleCode ? `(${f.vehicleCode})` : ''} {badge}
                                                        </option>
                                                    );
                                                })}
                                            </select>
                                            {errors.vehicleId && (
                                                <div style={{ color: '#ef4444', fontSize: '11px', fontWeight: 600, marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <span>⚠️</span> {errors.vehicleId}
                                                </div>
                                            )}
                                            {(() => {
                                                if (!form.vehicleId) return null;
                                                const currentVeh = vendorFleet.find(f => f._id === form.vehicleId);
                                                const comp = getVehicleCompliance(currentVeh, form.tripDate || form.startDate);
                                                if (!comp.isCompliant) {
                                                    return (
                                                        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '6px 10px', marginTop: 6, fontSize: 11, color: '#b91c1c', display: 'flex', alignItems: 'center', gap: 6 }}>
                                                            <span>🚫</span>
                                                            <span><strong>Vehicle Non-Compliant:</strong> {comp.expiredDocs.join(', ')} expired. Cannot be assigned.</span>
                                                        </div>
                                                    );
                                                }
                                                const conflict = checkAllocationConflict(form.vehicleId, 'vehicle', form, trips, data?._id);
                                                if (conflict.hasConflict && (data?.vehicleId?._id || data?.vehicleId) !== form.vehicleId) {
                                                    return (
                                                        <div style={{ background: '#fff7ed', border: '1px solid #ffedd5', borderRadius: 8, padding: '6px 10px', marginTop: 6, fontSize: 11, color: '#c2410c', display: 'flex', alignItems: 'center', gap: 6 }}>
                                                            <span>🚫</span>
                                                            <span><strong>Schedule Conflict:</strong> {conflict.reason}</span>
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            })()}
                                            {form.vehicleId && (
                                                <div style={{ marginTop: 8 }}>
                                                    <div style={{ display: 'flex', gap: 2, height: 6, borderRadius: 3, overflow: 'hidden' }}>
                                                        {Array.from({ length: 24 }).map((_, h) => {
                                                            const sh = form.startTime ? parseInt(form.startTime.split(':')[0]) : -1;
                                                            const eh = (form.endTime || form.startTime) ? parseInt((form.endTime || form.startTime).split(':')[0]) : -1;
                                                            const inWindow = sh !== -1 && h >= sh && h <= eh;
                                                            const targetDate = form.tripDate ? String(form.tripDate).split('T')[0] : null;
                                                            const busy = trips.some(t => {
                                                                const isSameEntity = (t.vehicleId?._id === form.vehicleId || t.vehicleId === form.vehicleId || t.vendorProposedVehicleId?._id === form.vehicleId || t.vendorProposedVehicleId === form.vehicleId);
                                                                if (!isSameEntity || !['Scheduled', 'Ongoing'].includes(t.status) || t._id === form._id) return false;

                                                                const isConflictDate = (
                                                                    (t.tripFrequency === 'One Time' && targetDate && t.tripDate && t.tripDate.startsWith(targetDate)) ||
                                                                    (t.tripFrequency === 'Recurring' && form.tripFrequency === 'Recurring' && t.recurringDays?.some(d => form.recurringDays?.includes(d))) ||
                                                                    (t.tripFrequency === 'Recurring' && form.tripFrequency === 'One Time' && targetDate && t.recurringDays?.includes(new Date(targetDate).toLocaleDateString('en-US', { weekday: 'short' }))) ||
                                                                    (t.tripFrequency === 'One Time' && form.tripFrequency === 'Recurring' && t.tripDate && form.recurringDays?.includes(new Date(t.tripDate).toLocaleDateString('en-US', { weekday: 'short' })))
                                                                );

                                                                if (!isConflictDate) return false;

                                                                return (t.billingType === 'Monthly' || (t.startTime && h >= parseInt(t.startTime.split(':')[0]) && h <= parseInt((t.endTime || t.startTime).split(':')[0])));
                                                            });
                                                            return <div key={h} title={busy ? `Busy at ${h}:00` : `Available at ${h}:00`} style={{ flex: 1, background: busy ? '#ef4444' : (inWindow ? '#16539C' : '#e2e8f0'), borderTop: inWindow ? '2px solid #fff' : 'none' }} />;
                                                        })}
                                                    </div>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 3, fontSize: 8, color: '#94a3b8', fontWeight: 600 }}>
                                                        <span>12AM</span><span>6AM</span><span>12PM</span><span>6PM</span><span>11PM</span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginTop: 10 }}>
                                        <div className="md-form-group">
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                                                <label className="md-label" style={{ margin: 0 }}>Vehicle</label>
                                                <button type="button" onClick={() => setAvailPanel('fleet')} title="Check fleet availability" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 7, border: '1px solid #c3dcf5', background: '#eef2ff', color: '#16539C', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                                                    <Icon d={icons.calendar} size={11} strokeWidth={2} /> Availability
                                                </button>
                                            </div>
                                            <select className="md-input" style={{
                                                borderColor: (errors.vehicleId || checkAllocationConflict(form.vehicleId, 'vehicle', form, trips, data?._id).hasConflict) ? '#ef4444' : '#e2e8f0',
                                                backgroundColor: (errors.vehicleId || checkAllocationConflict(form.vehicleId, 'vehicle', form, trips, data?._id).hasConflict) ? '#fef2f2' : undefined,
                                                boxShadow: (errors.vehicleId || checkAllocationConflict(form.vehicleId, 'vehicle', form, trips, data?._id).hasConflict) ? '0 0 0 3px rgba(239, 68, 68, 0.15)' : undefined
                                            }} value={form.vehicleId} onChange={e => {
                                                const val = e.target.value;
                                                let nextDriverId = form.driverId;
                                                if (val && Array.isArray(mappings)) {
                                                    const m = mappings.find(x => x.isActive !== false && (x.vehicleId?._id || x.vehicleId) === val);
                                                    if (m) {
                                                        const potentialDrvId = m.driverId?._id || m.driverId;
                                                        const drvObj = drivers.find(d => d._id === potentialDrvId);
                                                        const drvComp = getDriverCompliance(drvObj, form.tripDate || form.startDate);
                                                        const drvConflict = checkAllocationConflict(potentialDrvId, 'driver', form, trips, data?._id);
                                                        if (drvComp.isCompliant && !drvConflict.hasConflict) {
                                                            nextDriverId = potentialDrvId || form.driverId;
                                                        }
                                                    }
                                                }
                                                if (val && !isVendorTrip && nextDriverId === form.driverId && form.driverId) {
                                                    const drvVeh = getDriverMappedVehicleId(form.driverId);
                                                    if (drvVeh && drvVeh !== val) nextDriverId = '';
                                                }
                                                if (!val && form.driverId && getDriverMappedVehicleId(form.driverId)) nextDriverId = '';
                                                setForm(prev => ({ ...prev, vehicleId: val, driverId: nextDriverId }));
                                                if (errors.vehicleId) {
                                                    setErrors(prev => { const n = { ...prev }; delete n.vehicleId; return n; });
                                                }
                                            }}>
                                                <option value="">Select Vehicle</option>
                                                {vehicles.map(v => {
                                                    const comp = getVehicleCompliance(v, form.tripDate || form.startDate);
                                                    const conflict = checkAllocationConflict(v._id, 'vehicle', form, trips, data?._id);
                                                    const isCurrent = (data?.vehicleId?._id || data?.vehicleId) === v._id;
                                                    const isDisabled = (!comp.isCompliant || conflict.hasConflict) && !isCurrent;
                                                    const badge = !comp.isCompliant ? comp.label : (conflict.hasConflict ? conflict.label : '');
                                                    return (
                                                        <option key={v._id} value={v._id} disabled={isDisabled} style={isDisabled ? { color: '#94a3b8' } : {}}>
                                                            {v.registrationNumber || v.plateNumber} - {v.make} {v.vehicleCode ? `(${v.vehicleCode})` : ''} {badge}
                                                        </option>
                                                    );
                                                })}
                                            </select>
                                            {errors.vehicleId && (
                                                <div style={{ color: '#ef4444', fontSize: '11px', fontWeight: 600, marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <span>⚠️</span> {errors.vehicleId}
                                                </div>
                                            )}
                                            {(() => {
                                                if (!form.vehicleId) return null;
                                                const currentVeh = vehicles.find(v => v._id === form.vehicleId);
                                                const comp = getVehicleCompliance(currentVeh, form.tripDate || form.startDate);
                                                if (!comp.isCompliant) {
                                                    return (
                                                        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '6px 10px', marginTop: 6, fontSize: 11, color: '#b91c1c', display: 'flex', alignItems: 'center', gap: 6 }}>
                                                            <span>🚫</span>
                                                            <span><strong>Vehicle Non-Compliant:</strong> {comp.expiredDocs.join(', ')} expired. Cannot be assigned.</span>
                                                        </div>
                                                    );
                                                }
                                                const conflict = checkAllocationConflict(form.vehicleId, 'vehicle', form, trips, data?._id);
                                                if (conflict.hasConflict && (data?.vehicleId?._id || data?.vehicleId) !== form.vehicleId) {
                                                    return (
                                                        <div style={{ background: '#fff7ed', border: '1px solid #ffedd5', borderRadius: 8, padding: '6px 10px', marginTop: 6, fontSize: 11, color: '#c2410c', display: 'flex', alignItems: 'center', gap: 6 }}>
                                                            <span>🚫</span>
                                                            <span><strong>Schedule Conflict:</strong> {conflict.reason}</span>
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            })()}
                                            {form.vehicleId && (
                                                <div style={{ marginTop: 8 }}>
                                                    <div style={{ display: 'flex', gap: 2, height: 6, borderRadius: 3, overflow: 'hidden' }}>
                                                        {Array.from({ length: 24 }).map((_, h) => {
                                                            const sh = form.startTime ? parseInt(form.startTime.split(':')[0]) : -1;
                                                            const eh = (form.endTime || form.startTime) ? parseInt((form.endTime || form.startTime).split(':')[0]) : -1;
                                                            const inWindow = sh !== -1 && h >= sh && h <= eh;
                                                            const targetDate = form.tripDate ? String(form.tripDate).split('T')[0] : null;
                                                            const busy = trips.some(t => {
                                                                const isSameEntity = (t.vehicleId?._id === form.vehicleId || t.vehicleId === form.vehicleId);
                                                                if (!isSameEntity || !['Scheduled', 'Ongoing'].includes(t.status) || t._id === form._id) return false;

                                                                const isConflictDate = (
                                                                    (t.tripFrequency === 'One Time' && targetDate && t.tripDate && t.tripDate.startsWith(targetDate)) ||
                                                                    (t.tripFrequency === 'Recurring' && form.tripFrequency === 'Recurring' && t.recurringDays?.some(d => form.recurringDays?.includes(d))) ||
                                                                    (t.tripFrequency === 'Recurring' && form.tripFrequency === 'One Time' && targetDate && t.recurringDays?.includes(new Date(targetDate).toLocaleDateString('en-US', { weekday: 'short' }))) ||
                                                                    (t.tripFrequency === 'One Time' && form.tripFrequency === 'Recurring' && t.tripDate && form.recurringDays?.includes(new Date(t.tripDate).toLocaleDateString('en-US', { weekday: 'short' })))
                                                                );

                                                                if (!isConflictDate) return false;

                                                                return (t.billingType === 'Monthly' || (t.startTime && h >= parseInt(t.startTime.split(':')[0]) && h <= parseInt((t.endTime || t.startTime).split(':')[0])));
                                                            });
                                                            return <div key={h} title={busy ? `Busy at ${h}:00` : `Available at ${h}:00`} style={{ flex: 1, background: busy ? '#ef4444' : (inWindow ? '#16539C' : '#e2e8f0'), borderTop: inWindow ? '2px solid #fff' : 'none' }} />;
                                                        })}
                                                    </div>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 3, fontSize: 8, color: '#94a3b8', fontWeight: 600 }}>
                                                        <span>12AM</span><span>6AM</span><span>12PM</span><span>6PM</span><span>11PM</span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        <div className="md-form-group">
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                                                <label className="md-label" style={{ margin: 0 }}>Driver</label>
                                                <button type="button" onClick={() => setAvailPanel('drivers')} title="Check driver availability" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 7, border: '1px solid #c3dcf5', background: '#eef2ff', color: '#16539C', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                                                    <Icon d={icons.calendar} size={11} strokeWidth={2} /> Availability
                                                </button>
                                            </div>
                                            <select className="md-input" style={{
                                                borderColor: (errors.driverId || checkAllocationConflict(form.driverId, 'driver', form, trips, data?._id).hasConflict) ? '#ef4444' : '#e2e8f0',
                                                backgroundColor: (errors.driverId || checkAllocationConflict(form.driverId, 'driver', form, trips, data?._id).hasConflict) ? '#fef2f2' : undefined,
                                                boxShadow: (errors.driverId || checkAllocationConflict(form.driverId, 'driver', form, trips, data?._id).hasConflict) ? '0 0 0 3px rgba(239, 68, 68, 0.15)' : undefined
                                            }} value={form.driverId} onChange={e => {
                                                const val = e.target.value;
                                                let nextVehicleId = form.vehicleId;
                                                if (val && Array.isArray(mappings)) {
                                                    const m = mappings.find(x => x.isActive !== false && (x.driverId?._id || x.driverId) === val);
                                                    if (m) {
                                                        const potentialVehId = m.vehicleId?._id || m.vehicleId;
                                                        const vehObj = vehicles.find(v => v._id === potentialVehId);
                                                        const vehComp = getVehicleCompliance(vehObj, form.tripDate || form.startDate);
                                                        const vehConflict = checkAllocationConflict(potentialVehId, 'vehicle', form, trips, data?._id);
                                                        if (vehComp.isCompliant && !vehConflict.hasConflict) {
                                                            nextVehicleId = potentialVehId || form.vehicleId;
                                                        }
                                                    }
                                                }
                                                setForm(prev => ({ ...prev, driverId: val, vehicleId: nextVehicleId }));
                                                if (errors.driverId) {
                                                    setErrors(prev => { const n = { ...prev }; delete n.driverId; return n; });
                                                }
                                            }}>
                                                <option value="">Select Driver</option>
                                                {drivers.map(d => {
                                                    let isLeave = false;
                                                    if (d.leaves && d.leaves.length > 0) {
                                                        const targetTime = form.tripDate ? new Date(String(form.tripDate).split('T')[0]).getTime() : new Date().setHours(0,0,0,0);
                                                        isLeave = d.leaves.some(l => {
                                                            if (l.status !== 'Approved') return false;
                                                            const st = new Date(l.startDate).setHours(0,0,0,0);
                                                            const en = new Date(l.endDate).setHours(23,59,59,999);
                                                            return targetTime >= st && targetTime <= en;
                                                        });
                                                    }
                                                    const comp = getDriverCompliance(d, form.tripDate || form.startDate);
                                                    const conflict = checkAllocationConflict(d._id, 'driver', form, trips, data?._id);
                                                    const isCurrent = (data?.driverId?._id || data?.driverId) === d._id;
                                                    const drvMappedVeh = getDriverMappedVehicleId(d._id);
                                                    const mappedElsewhere = !!form.vehicleId && !isVendorTrip && !!drvMappedVeh && drvMappedVeh !== form.vehicleId && !isCurrent;
                                                    const lockedOut = (driverLocked && d._id !== vehicleMapping.driverId) || mappedElsewhere;
                                                    const isDisabled = ((!comp.isCompliant || isLeave || conflict.hasConflict) && !isCurrent) || lockedOut;
                                                    const leaveLabel = isLeave ? '⚠️ (On Leave - Not Allowed)' : '';
                                                    const badge = !comp.isCompliant ? comp.label : (leaveLabel || (conflict.hasConflict ? conflict.label : (mappedElsewhere ? `🔒 (Mapped to ${vehicleLabel(drvMappedVeh)})` : (lockedOut ? '(Vehicle mapped to another driver)' : (driverLocked ? '🔗 Mapped' : '')))));

                                                    return (
                                                        <option key={d._id} value={d._id} disabled={isDisabled} style={isDisabled ? { color: '#94a3b8' } : {}}>
                                                            {d.name} {d.driverCode ? `(${d.driverCode})` : ''} {badge}
                                                        </option>
                                                    );
                                                })}
                                            </select>
                                            {form.vehicleId && !isVendorTrip && (
                                                <div style={{ fontSize: 11, marginTop: 5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4,
                                                    color: vehicleMapping.status === 'locked' ? '#047857' : vehicleMapping.status === 'unavailable' ? '#b45309' : '#64748b' }}>
                                                    {vehicleMapping.status === 'locked' && (
                                                        <span>🔗 Mapped driver: {vehicleMapping.name}{vehicleMapping.code ? ` (${vehicleMapping.code})` : ''} — locked to this vehicle</span>
                                                    )}
                                                    {vehicleMapping.status === 'unavailable' && (
                                                        <span>⚠️ Mapped driver {vehicleMapping.name} unavailable ({vehicleMapping.reason}) — you can select another driver</span>
                                                    )}
                                                    {vehicleMapping.status === 'none' && (
                                                        <span>No driver mapped to this vehicle — select any unmapped driver (drivers mapped to other vehicles are locked)</span>
                                                    )}
                                                </div>
                                            )}
                                            {errors.driverId && (
                                                <div style={{ color: '#ef4444', fontSize: '11px', fontWeight: 600, marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <span>⚠️</span> {errors.driverId}
                                                </div>
                                            )}
                                            {(() => {
                                                if (!form.driverId) return null;
                                                const currentDrv = drivers.find(d => d._id === form.driverId);
                                                const comp = getDriverCompliance(currentDrv, form.tripDate || form.startDate);
                                                if (!comp.isCompliant) {
                                                    return (
                                                        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '6px 10px', marginTop: 6, fontSize: 11, color: '#b91c1c', display: 'flex', alignItems: 'center', gap: 6 }}>
                                                            <span>🚫</span>
                                                            <span><strong>Driver Non-Compliant:</strong> {comp.expiredDocs.join(', ')} expired. Cannot be assigned.</span>
                                                        </div>
                                                    );
                                                }
                                                const conflict = checkAllocationConflict(form.driverId, 'driver', form, trips, data?._id);
                                                if (conflict.hasConflict && (data?.driverId?._id || data?.driverId) !== form.driverId) {
                                                    return (
                                                        <div style={{ background: '#fff7ed', border: '1px solid #ffedd5', borderRadius: 8, padding: '6px 10px', marginTop: 6, fontSize: 11, color: '#c2410c', display: 'flex', alignItems: 'center', gap: 6 }}>
                                                            <span>🚫</span>
                                                            <span><strong>Schedule Conflict:</strong> {conflict.reason}</span>
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            })()}
                                            {form.driverId && (
                                                <div style={{ marginTop: 8 }}>
                                                    <div style={{ display: 'flex', gap: 2, height: 6, borderRadius: 3, overflow: 'hidden' }}>
                                                        {Array.from({ length: 24 }).map((_, h) => {
                                                            const sh = form.startTime ? parseInt(form.startTime.split(':')[0]) : -1;
                                                            const eh = (form.endTime || form.startTime) ? parseInt((form.endTime || form.startTime).split(':')[0]) : -1;
                                                            const inWindow = sh !== -1 && h >= sh && h <= eh;
                                                            const targetDate = form.tripDate ? String(form.tripDate).split('T')[0] : null;
                                                            const busy = trips.some(t => {
                                                                const isSameEntity = (t.driverId?._id === form.driverId || t.driverId === form.driverId || t.vendorProposedDriverId?._id === form.driverId || t.vendorProposedDriverId === form.driverId);
                                                                if (!isSameEntity || !['Scheduled', 'Ongoing'].includes(t.status) || t._id === form._id) return false;

                                                                const isConflictDate = (
                                                                    (t.tripFrequency === 'One Time' && targetDate && t.tripDate && t.tripDate.startsWith(targetDate)) ||
                                                                    (t.tripFrequency === 'Recurring' && form.tripFrequency === 'Recurring' && t.recurringDays?.some(d => form.recurringDays?.includes(d))) ||
                                                                    (t.tripFrequency === 'Recurring' && form.tripFrequency === 'One Time' && targetDate && t.recurringDays?.includes(new Date(targetDate).toLocaleDateString('en-US', { weekday: 'short' }))) ||
                                                                    (t.tripFrequency === 'One Time' && form.tripFrequency === 'Recurring' && t.tripDate && form.recurringDays?.includes(new Date(t.tripDate).toLocaleDateString('en-US', { weekday: 'short' })))
                                                                );

                                                                if (!isConflictDate) return false;

                                                                return (t.billingType === 'Monthly' || (t.startTime && h >= parseInt(t.startTime.split(':')[0]) && h <= parseInt((t.endTime || t.startTime).split(':')[0])));
                                                            });
                                                            return <div key={h} title={busy ? `Busy at ${h}:00` : `Available at ${h}:00`} style={{ flex: 1, background: busy ? '#ef4444' : (inWindow ? '#16539C' : '#e2e8f0'), borderTop: inWindow ? '2px solid #fff' : 'none' }} />;
                                                        })}
                                                    </div>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 3, fontSize: 8, color: '#94a3b8', fontWeight: 600 }}>
                                                        <span>12AM</span><span>6AM</span><span>12PM</span><span>6PM</span><span>11PM</span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {!isDispatchOnly && (
                                <>
                                    <div className="md-form-group pickup-search-container" style={{ gridColumn: 'span 2', position: 'relative' }}>
                                        <label className="md-label" style={{ display: 'flex', justifyContent: 'space-between', color: errors.pickupLocation ? '#dc2626' : undefined }}>
                                            Pickup (Autocomplete)*
                                            {form.pickupLat && <span style={{ fontSize: '10px', color: '#10b981' }}>Selected</span>}
                                        </label>
                                        <div style={{ position: 'relative' }}>
                                            <input
                                                className="md-input"
                                                value={pickupQuery}
                                                onChange={e => {
                                                    const val = e.target.value;
                                                    setPickupQuery(val);
                                                    setForm(prev => ({
                                                        ...prev,
                                                        pickupLocation: val,
                                                        pickupLat: form.pickupLat ? null : prev.pickupLat,
                                                        pickupLng: form.pickupLng ? null : prev.pickupLng
                                                    }));
                                                    if (errors.pickupLocation && val.trim()) {
                                                        setErrors(prev => { const n = { ...prev }; delete n.pickupLocation; return n; });
                                                    }
                                                }}
                                                onFocus={() => { if (pickupSuggestions.length > 0) setShowPickupSuggestions(true); }}
                                                placeholder="Search for pickup point..."
                                                style={{
                                                    paddingLeft: '32px',
                                                    borderColor: errors.pickupLocation ? '#ef4444' : undefined,
                                                    backgroundColor: errors.pickupLocation ? '#fef2f2' : undefined,
                                                    boxShadow: errors.pickupLocation ? '0 0 0 3px rgba(239, 68, 68, 0.15)' : undefined
                                                }}
                                            />
                                            <div style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: errors.pickupLocation ? '#ef4444' : '#94a3b8' }}>
                                                <Icon d={icons.search} size={14} />
                                            </div>
                                        </div>
                                        {errors.pickupLocation && (
                                            <div style={{ color: '#ef4444', fontSize: '11px', fontWeight: 600, marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <span>⚠️</span> {errors.pickupLocation}
                                            </div>
                                        )}
                                        {showPickupSuggestions && pickupSuggestions.length > 0 && (
                                            <ul style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', zIndex: 1000, margin: 0, padding: 0, listStyle: 'none', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', maxHeight: '200px', overflowY: 'auto' }}>
                                                {pickupSuggestions.map(place => (
                                                    <li key={place.place_id} onClick={() => handleSelectLocation(place, 'pickup')} style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', fontSize: '12px', color: '#334155' }}>
                                                        {place.display_name}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                    <div className="md-form-group drop-search-container" style={{ gridColumn: 'span 2', position: 'relative' }}>
                                        <label className="md-label" style={{ display: 'flex', justifyContent: 'space-between', color: errors.dropLocation ? '#dc2626' : undefined }}>
                                            Drop (Autocomplete)*
                                            {form.dropLat && <span style={{ fontSize: '10px', color: '#10b981' }}>Selected</span>}
                                        </label>
                                        <div style={{ position: 'relative' }}>
                                            <input
                                                className="md-input"
                                                value={dropQuery}
                                                onChange={e => {
                                                    const val = e.target.value;
                                                    setDropQuery(val);
                                                    setForm(prev => ({
                                                        ...prev,
                                                        dropLocation: val,
                                                        dropLat: form.dropLat ? null : prev.dropLat,
                                                        dropLng: form.dropLng ? null : prev.dropLng
                                                    }));
                                                    if (errors.dropLocation && val.trim()) {
                                                        setErrors(prev => { const n = { ...prev }; delete n.dropLocation; return n; });
                                                    }
                                                }}
                                                onFocus={() => { if (dropSuggestions.length > 0) setShowDropSuggestions(true); }}
                                                placeholder="Search for destination..."
                                                style={{
                                                    paddingLeft: '32px',
                                                    borderColor: errors.dropLocation ? '#ef4444' : undefined,
                                                    backgroundColor: errors.dropLocation ? '#fef2f2' : undefined,
                                                    boxShadow: errors.dropLocation ? '0 0 0 3px rgba(239, 68, 68, 0.15)' : undefined
                                                }}
                                            />
                                            <div style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: errors.dropLocation ? '#ef4444' : '#94a3b8' }}>
                                                <Icon d={icons.search} size={14} />
                                            </div>
                                        </div>
                                        {errors.dropLocation && (
                                            <div style={{ color: '#ef4444', fontSize: '11px', fontWeight: 600, marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <span>⚠️</span> {errors.dropLocation}
                                            </div>
                                        )}
                                        {showDropSuggestions && dropSuggestions.length > 0 && (
                                            <ul style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', zIndex: 1000, margin: 0, padding: 0, listStyle: 'none', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', maxHeight: '200px', overflowY: 'auto' }}>
                                                {dropSuggestions.map(place => (
                                                    <li key={place.place_id} onClick={() => handleSelectLocation(place, 'drop')} style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', fontSize: '12px', color: '#334155' }}>
                                                        {place.display_name}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </>
                            )}
                            {!isDispatchOnly && (
                                <>
                                    <div className="md-form-group">
                                        <label className="md-label">Frequency*</label>
                                        <select className="md-input" value={form.tripFrequency} onChange={e => setForm({ ...form, tripFrequency: e.target.value })}>
                                            <option value="One Time">One Time</option>
                                            <option value="Recurring">Recurring</option>
                                        </select>
                                    </div>
                                    {form.tripFrequency === 'One Time' ? (
                                        <div className="md-form-group">
                                            <label className="md-label" style={{ color: errors.tripDate ? '#dc2626' : undefined }}>Trip Date*</label>
                                            <div style={{ position: 'relative' }}>
                                                <input 
                                                    type="text" 
                                                    className="md-input" 
                                                    placeholder="DD/MM/YYYY"
                                                    value={formatDateToDMY(form.tripDate)} 
                                                    onChange={e => handleDateChange('tripDate', e.target.value, e.nativeEvent)} 
                                                    style={{
                                                        paddingRight: '40px',
                                                        borderColor: errors.tripDate ? '#ef4444' : undefined,
                                                        backgroundColor: errors.tripDate ? '#fef2f2' : undefined,
                                                        boxShadow: errors.tripDate ? '0 0 0 3px rgba(239, 68, 68, 0.15)' : undefined
                                                    }}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => tripDateRef.current && tripDateRef.current.showPicker()}
                                                    style={{
                                                        position: 'absolute',
                                                        right: '12px',
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        background: 'none',
                                                        border: 'none',
                                                        cursor: 'pointer',
                                                        color: errors.tripDate ? '#ef4444' : '#64748b',
                                                        padding: 0,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}
                                                >
                                                    <Icon d={icons.calendar} size={16} />
                                                </button>
                                                <input 
                                                    type="date"
                                                    ref={tripDateRef}
                                                    style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none', bottom: 0, right: 0 }}
                                                    value={form.tripDate ? String(form.tripDate).split('T')[0] : ''}
                                                    onChange={e => {
                                                        setForm({ ...form, tripDate: e.target.value });
                                                        if (errors.tripDate) {
                                                            setErrors(prev => { const n = { ...prev }; delete n.tripDate; return n; });
                                                        }
                                                    }}
                                                />
                                            </div>
                                            {errors.tripDate && (
                                                <div style={{ color: '#ef4444', fontSize: '11px', fontWeight: 600, marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <span>⚠️</span> {errors.tripDate}
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <>
                                            <div className="md-form-group" style={{ gridColumn: 'span 2' }}>
                                                <label className="md-label">Recurring Days</label>
                                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                                                        <button
                                                            key={day}
                                                            type="button"
                                                            onClick={() => toggleDay(day)}
                                                            style={{
                                                                flex: 1, minWidth: 40, padding: '8px 0', borderRadius: 8, border: '1px solid #e2e8f0',
                                                                background: form.recurringDays.includes(day) ? '#063A5C' : '#fff',
                                                                color: form.recurringDays.includes(day) ? '#fff' : '#64748b',
                                                                fontSize: 12, fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s'
                                                            }}
                                                        >
                                                            {day.charAt(0)}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                            <div className="md-form-group">
                                                <label className="md-label" style={{ color: errors.startDate ? '#dc2626' : undefined }}>Effective From*</label>
                                                <div style={{ position: 'relative' }}>
                                                    <input 
                                                        type="text" 
                                                        className="md-input" 
                                                        placeholder="DD/MM/YYYY"
                                                        value={formatDateToDMY(form.startDate)} 
                                                        onChange={e => handleDateChange('startDate', e.target.value, e.nativeEvent)} 
                                                        style={{
                                                            paddingRight: '40px',
                                                            borderColor: errors.startDate ? '#ef4444' : undefined,
                                                            backgroundColor: errors.startDate ? '#fef2f2' : undefined,
                                                            boxShadow: errors.startDate ? '0 0 0 3px rgba(239, 68, 68, 0.15)' : undefined
                                                        }}
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => startDateRef.current && startDateRef.current.showPicker()}
                                                        style={{
                                                            position: 'absolute',
                                                            right: '12px',
                                                            top: '50%',
                                                            transform: 'translateY(-50%)',
                                                            background: 'none',
                                                            border: 'none',
                                                            cursor: 'pointer',
                                                            color: errors.startDate ? '#ef4444' : '#64748b',
                                                            padding: 0,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}
                                                    >
                                                        <Icon d={icons.calendar} size={16} />
                                                    </button>
                                                    <input 
                                                        type="date"
                                                        ref={startDateRef}
                                                        style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none', bottom: 0, right: 0 }}
                                                        value={form.startDate ? String(form.startDate).split('T')[0] : ''}
                                                        onChange={e => {
                                                            setForm({ ...form, startDate: e.target.value });
                                                            if (errors.startDate) {
                                                                setErrors(prev => { const n = { ...prev }; delete n.startDate; return n; });
                                                            }
                                                        }}
                                                    />
                                                </div>
                                                {errors.startDate && (
                                                    <div style={{ color: '#ef4444', fontSize: '11px', fontWeight: 600, marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                        <span>⚠️</span> {errors.startDate}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="md-form-group">
                                                <label className="md-label" style={{ color: errors.endDate ? '#dc2626' : undefined }}>Effective To</label>
                                                <div style={{ position: 'relative' }}>
                                                    <input 
                                                        type="text" 
                                                        className="md-input" 
                                                        placeholder="DD/MM/YYYY"
                                                        value={formatDateToDMY(form.endDate)} 
                                                        onChange={e => handleDateChange('endDate', e.target.value, e.nativeEvent)} 
                                                        style={{
                                                            paddingRight: '40px',
                                                            borderColor: errors.endDate ? '#ef4444' : undefined,
                                                            backgroundColor: errors.endDate ? '#fef2f2' : undefined,
                                                            boxShadow: errors.endDate ? '0 0 0 3px rgba(239, 68, 68, 0.15)' : undefined
                                                        }}
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => endDateRef.current && endDateRef.current.showPicker()}
                                                        style={{
                                                            position: 'absolute',
                                                            right: '12px',
                                                            top: '50%',
                                                            transform: 'translateY(-50%)',
                                                            background: 'none',
                                                            border: 'none',
                                                            cursor: 'pointer',
                                                            color: errors.endDate ? '#ef4444' : '#64748b',
                                                            padding: 0,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}
                                                    >
                                                        <Icon d={icons.calendar} size={16} />
                                                    </button>
                                                    <input 
                                                        type="date"
                                                        ref={endDateRef}
                                                        style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none', bottom: 0, right: 0 }}
                                                        value={form.endDate ? String(form.endDate).split('T')[0] : ''}
                                                        onChange={e => {
                                                            setForm({ ...form, endDate: e.target.value });
                                                            if (errors.endDate) {
                                                                setErrors(prev => { const n = { ...prev }; delete n.endDate; return n; });
                                                            }
                                                        }}
                                                    />
                                                </div>
                                                {errors.endDate && (
                                                    <div style={{ color: '#ef4444', fontSize: '11px', fontWeight: 600, marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                        <span>⚠️</span> {errors.endDate}
                                                    </div>
                                                )}
                                            </div>
                                        </>
                                    )}
                                    <div className="md-form-group">
                                        <label className="md-label" style={{ color: errors.startTime ? '#dc2626' : undefined }}>Start Time*</label>
                                        <input 
                                            type="time" 
                                            className="md-input" 
                                            style={{
                                                borderColor: errors.startTime ? '#ef4444' : undefined,
                                                backgroundColor: errors.startTime ? '#fef2f2' : undefined,
                                                boxShadow: errors.startTime ? '0 0 0 3px rgba(239, 68, 68, 0.15)' : undefined
                                            }}
                                            value={form.startTime || ''} 
                                            onChange={e => {
                                                const val = e.target.value;
                                                setForm({ ...form, startTime: val });
                                                if (errors.startTime) {
                                                    setErrors(prev => { const n = { ...prev }; delete n.startTime; return n; });
                                                }
                                            }} 
                                        />
                                        {errors.startTime && (
                                            <div style={{ color: '#ef4444', fontSize: '11px', fontWeight: 600, marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <span>⚠️</span> {errors.startTime}
                                            </div>
                                        )}
                                    </div>
                                    <div className="md-form-group">
                                        <label className="md-label" style={{ color: errors.endTime ? '#dc2626' : undefined }}>End Time*</label>
                                        <input 
                                            type="time" 
                                            className="md-input" 
                                            style={{
                                                borderColor: errors.endTime ? '#ef4444' : undefined,
                                                backgroundColor: errors.endTime ? '#fef2f2' : undefined,
                                                boxShadow: errors.endTime ? '0 0 0 3px rgba(239, 68, 68, 0.15)' : undefined
                                            }}
                                            value={form.endTime || ''} 
                                            onChange={e => {
                                                const val = e.target.value;
                                                setForm({ ...form, endTime: val });
                                                if (errors.endTime) {
                                                    setErrors(prev => { const n = { ...prev }; delete n.endTime; return n; });
                                                }
                                            }} 
                                        />
                                        {errors.endTime && (
                                            <div style={{ color: '#ef4444', fontSize: '11px', fontWeight: 600, marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <span>⚠️</span> {errors.endTime}
                                            </div>
                                        )}
                                    </div>
                                    <div className="md-form-group" style={{ gridColumn: 'span 2' }}>
                                        <label className="md-label">
                                            Trip Amount / Fee {['Daily', 'Ad-hoc'].includes(form.billingType) ? '(Daily)' : '(Monthly)'}
                                        </label>
                                        <input type="number" className="md-input" placeholder="0.00" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} />
                                    </div>
                                </>
                            )}
                            {data && !isDispatchOnly && data.vehicleId && (
                                <div className="md-form-group" style={{ gridColumn: 'span 2', marginTop: 10 }}>
                                    <label className="md-label" style={{ color: errors.changeReason ? '#dc2626' : '#ea580c' }}>Update Status / Change Reason*</label>
                                    <input 
                                        className="md-input" 
                                        style={{
                                            border: errors.changeReason ? '1.5px solid #ef4444' : '1px solid #fed7aa',
                                            background: errors.changeReason ? '#fef2f2' : '#fff7ed',
                                            boxShadow: errors.changeReason ? '0 0 0 3px rgba(239, 68, 68, 0.15)' : undefined
                                        }}
                                        placeholder="e.g. Changed driver due to shift update" 
                                        value={form.changeReason} 
                                        onChange={e => {
                                            const val = e.target.value;
                                            setForm({ ...form, changeReason: val });
                                            if (errors.changeReason) {
                                                setErrors(prev => { const n = { ...prev }; delete n.changeReason; return n; });
                                            }
                                        }} 
                                        required
                                    />
                                    {errors.changeReason && (
                                        <div style={{ color: '#ef4444', fontSize: '11px', fontWeight: 600, marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <span>⚠️</span> {errors.changeReason}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        {!isDispatchOnly && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
                                <div style={{ flex: 1, position: 'relative', borderRadius: 20, overflow: 'hidden', border: '1px solid #e2e8f0', background: '#f8fafc' }}>
                                    <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
                                    {!form.routeId && !form.pickupLocation && (
                                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(248,30,252,0.02)', backdropFilter: 'blur(2px)', color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: 40 }}>
                                            <div>
                                                <Icon d={icons.map} size={32} />
                                                <p style={{ marginTop: 10 }}>Select a route or locations to preview on map</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                {routeStops.length > 0 && (
                                    <div style={{ padding: '15px', background: '#f8fafc', borderRadius: 16, border: '1px solid #e2e8f0' }}>
                                        <div style={{ fontSize: 12, fontWeight: 700, color: '#063A5C', marginBottom: 10 }}>Route Stops ({routeStops.length})</div>
                                        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 5 }}>
                                            {routeStops.map((s, idx) => (
                                                <div key={s._id} style={{ background: '#fff', padding: '6px 12px', borderRadius: 8, border: '1px solid #e2e8f0', whiteSpace: 'nowrap', fontSize: 11, color: '#64748b' }}>
                                                    <span style={{ fontWeight: 700, marginRight: 5 }}>{idx + 1}.</span> {s.stopName}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="md-modal-footer" style={{ borderTop: '1px solid #f1f5f9', padding: '15px 25px' }}>
                        {!data && !isDispatchOnly && (
                            <span style={{ marginRight: 'auto', fontSize: 12, color: '#64748b', display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#10b981', display: 'inline-block' }} />
                                Draft auto-saved · kept for 24 hours
                            </span>
                        )}
                        <button className="md-cancel-btn" onClick={!data && !isDispatchOnly ? handleClearDraft : onClose} title={!data && !isDispatchOnly ? 'Discard and delete the saved draft' : undefined}>Discard</button>
                        <button className="md-save-btn" onClick={handleConfirm}>Confirm Trip Schedule</button>
                    </div>
                </div>
            </div>
        </>
    );
}

function ImportWizard({ trips, operatorId, onBack, onDone }) {
    const [file, setFile] = useState(null);
    const [dragActive, setDragActive] = useState(false);
    const [parsedData, setParsedData] = useState([]);
    const [importing, setImporting] = useState(false);
    const [result, setResult] = useState(null);

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            processFile(e.dataTransfer.files[0]);
        }
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            processFile(e.target.files[0]);
        }
    };

    const processFile = (selectedFile) => {
        setFile(selectedFile);
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const bstr = e.target.result;
                const wb = XLSX.read(bstr, { type: 'binary' });
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];
                const data = XLSX.utils.sheet_to_json(ws);

                const standardized = data.map((row) => {
                    const mapped = {};
                    Object.keys(row).forEach(key => {
                        const cleanKey = key.trim();
                        mapped[cleanKey] = row[key];
                    });
                    return mapped;
                });

                setParsedData(standardized);
            } catch (err) {
                console.error(err);
                alert("Failed to parse Excel file. Please ensure it is a valid .xlsx or .xls file.");
            }
        };
        reader.readAsBinaryString(selectedFile);
    };

    const downloadTemplate = () => {
        const templateData = [
            {
                'Client': 'Infosys Ltd',
                'Route': 'IT Park Shuttle',
                'Trip Type': 'Login',
                'Frequency': 'Recurring',
                'Recurring Days': 'Mon,Tue,Wed,Thu,Fri',
                'Trip Date': '',
                'Start Time': '08:00',
                'End Time': '09:00',
                'Vehicle Plate': 'DXB-1234',
                'Driver Name': 'John Doe',
                'Amount': 500,
                'Billing Category': 'Daily',
                'Status': 'Scheduled'
            }
        ];
        const ws = XLSX.utils.json_to_sheet(templateData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Trips');
        XLSX.writeFile(wb, 'Trips_Template.xlsx');
    };

    const handleImport = async () => {
        if (parsedData.length === 0) return;
        setImporting(true);
        try {
            const mapped = parsedData.map(row => ({
                client: row['Client'],
                route: row['Route'],
                tripType: row['Trip Type'] || 'Login',
                tripFrequency: row['Frequency'] || 'One Time',
                recurringDays: row['Recurring Days'] ? String(row['Recurring Days']).split(',').map(d => d.trim()) : [],
                tripDate: row['Trip Date'],
                startTime: row['Start Time'],
                endTime: row['End Time'],
                vehiclePlate: row['Vehicle Plate'],
                driverName: row['Driver Name'],
                amount: row['Amount'],
                billingType: row['Billing Category'] || 'Daily',
                status: row['Status'] || 'Scheduled'
            }));
            const res = await axios.post(`/api/trips/import`, { operatorId, trips: mapped });
            setResult({ createdCount: res.data.successCount, updatedCount: 0, errors: res.data.errors });
        } catch (err) {
            console.error(err);
            alert(err.response?.data?.message || "Import failed. Please try again.");
        } finally {
            setImporting(false);
        }
    };

    if (result) {
        return (
            <div className="md-page" style={{ fontFamily: "'Inter', sans-serif", padding: '30px', maxWidth: '1200px', margin: '0 auto' }}>
                <div style={{ background: 'white', borderRadius: '16px', padding: '40px', textAlign: 'center', boxShadow: '0 10px 25px rgba(0,0,0,0.05)', border: '1px solid #f1f5f9' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '80px', height: '80px', borderRadius: '50%', background: '#d1fae5', color: '#10b981', marginBottom: '24px' }}>
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                    </div>
                    <h2 style={{ fontSize: '28px', fontWeight: '800', color: '#1e293b', marginBottom: '12px' }}>Import Completed!</h2>
                    <p style={{ fontSize: '15px', color: '#64748b', maxWidth: '600px', margin: '0 auto 30px' }}>
                        The trip data has been processed.
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', maxWidth: '700px', margin: '0 auto 30px' }}>
                        <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
                            <div style={{ fontSize: '32px', fontWeight: '800', color: '#065f46' }}>{result.updatedCount || 0}</div>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: '#047857', marginTop: '4px' }}>Trips Updated</div>
                        </div>
                        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
                            <div style={{ fontSize: '32px', fontWeight: '800', color: '#1e40af' }}>{result.createdCount || 0}</div>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: '#1d4ed8', marginTop: '4px' }}>New Trips Created</div>
                        </div>
                        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
                            <div style={{ fontSize: '32px', fontWeight: '800', color: '#92400e' }}>{(result.errors?.length) || 0}</div>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: '#b45309', marginTop: '4px' }}>Skipped / Warnings</div>
                        </div>
                    </div>
                    <button onClick={onDone} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '12px 30px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg,#16539C,#00B2A9)', color: 'white', fontSize: '15px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 12px rgba(79,70,229,0.3)', transition: 'all 0.15s' }}>
                        Return to Trips
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="md-page" style={{ fontFamily: "'Inter', sans-serif", padding: '30px', maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '30px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <button onClick={onBack} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '36px', height: '36px', borderRadius: '10px', border: '1px solid #e2e8f0', background: 'white', color: '#64748b', cursor: 'pointer', transition: 'all 0.15s' }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
                    </button>
                    <div>
                        <h1 style={{ fontSize: '24px', fontWeight: '800', color: '#1e293b', margin: 0 }}>Import Trips</h1>
                        <p style={{ fontSize: '13px', color: '#64748b', margin: '4px 0 0' }}>Bulk import new trips via Excel upload.</p>
                    </div>
                </div>
            </div>

            {parsedData.length === 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: '400px 1fr', gap: '30px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', padding: '24px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                            <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>ℹ️ How it works</h3>
                            <ul style={{ paddingLeft: '20px', margin: 0, display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '13.5px', color: '#475569', lineHeight: '1.5' }}>
                                <li>The file must be an Excel sheet (<code>.xlsx</code> or <code>.xls</code>).</li>
                                <li>All valid rows will automatically be created as brand new trips!</li>
                            </ul>
                        </div>
                        <div style={{ background: '#f8fafc', borderRadius: '16px', border: '1px dashed #cbd5e1', padding: '20px', textAlign: 'center' }}>
                            <p style={{ fontSize: '13px', color: '#475569', margin: '0 0 12px' }}>Need a starting point? Download our pre-configured format template.</p>
                            <button onClick={downloadTemplate} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 16px', borderRadius: '8px', border: '1.5px solid #cbd5e1', background: 'white', color: '#334155', fontSize: '12.5px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.15s' }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                                Download Sample Template
                            </button>
                        </div>
                    </div>
                    <div
                        onDragEnter={handleDrag} onDragOver={handleDrag} onDragLeave={handleDrag} onDrop={handleDrop}
                        style={{
                            background: dragActive ? '#f0f7ff' : 'white', borderRadius: '16px', border: `2px dashed ${dragActive ? '#16539C' : '#cbd5e1'}`,
                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 40px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', transition: 'all 0.15s', cursor: 'pointer'
                        }}
                        onClick={() => document.getElementById('file-upload-input').click()}
                    >
                        <input id="file-upload-input" type="file" accept=".xlsx,.xls" onChange={handleFileChange} style={{ display: 'none' }} />
                        <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '64px', height: '64px', borderRadius: '16px', background: '#eef2ff', color: '#16539C', marginBottom: '20px' }}>
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
                        </div>
                        <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', margin: '0 0 8px' }}>Drag & Drop Excel File</h3>
                        <p style={{ fontSize: '13.5px', color: '#64748b', margin: '0 0 16px' }}>or click to browse your local files</p>
                        <span style={{ fontSize: '12px', color: '#94a3b8', background: '#f8fafc', padding: '6px 12px', borderRadius: '6px', border: '1px solid #f1f5f9' }}>Supports .xlsx, .xls formats</span>
                    </div>
                </div>
            ) : (
                <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span style={{ fontSize: '15px', fontWeight: '700', color: '#1e293b' }}>{file?.name}</span>
                                <span style={{ fontSize: '12px', color: '#64748b', background: '#cbd5e1', padding: '2px 8px', borderRadius: '999px', fontWeight: '600' }}>{parsedData.length} Rows Found</span>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button onClick={() => { setFile(null); setParsedData([]); }} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '9px 18px', borderRadius: '10px', border: '1.5px solid #cbd5e1', background: 'white', color: '#475569', fontSize: '13px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.15s' }}>Change File</button>
                            <button onClick={handleImport} disabled={importing} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '9px 22px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg,#16539C,#00B2A9)', color: 'white', fontSize: '13px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 12px rgba(79,70,229,0.3)', transition: 'all 0.15s' }}>
                                {importing ? 'Processing...' : `Proceed Import (${parsedData.length})`}
                            </button>
                        </div>
                    </div>
                    <div style={{ overflowX: 'auto', maxHeight: '500px' }}>
                        <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: '800px' }}>
                            <thead>
                                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                    <th style={{ padding: '14px 20px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Client</th>
                                    <th style={{ padding: '14px 20px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Route</th>
                                    <th style={{ padding: '14px 20px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Vehicle Plate</th>
                                    <th style={{ padding: '14px 20px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Driver Name</th>
                                    <th style={{ padding: '14px 20px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Start Time</th>
                                    <th style={{ padding: '14px 20px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {parsedData.map((d, i) => (
                                    <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', background: '#fff' }}>
                                        <td style={{ padding: '14px 20px', fontWeight: '700', color: '#1e293b' }}>{d.Client || d['Client'] || '—'}</td>
                                        <td style={{ padding: '14px 20px', color: '#475569', fontSize: '13px' }}>{d.Route || d['Route'] || '—'}</td>
                                        <td style={{ padding: '14px 20px', color: '#475569', fontSize: '13px' }}>{d.VehiclePlate || d['Vehicle Plate'] || '—'}</td>
                                        <td style={{ padding: '14px 20px', color: '#475569', fontSize: '13px' }}>{d.DriverName || d['Driver Name'] || '—'}</td>
                                        <td style={{ padding: '14px 20px', color: '#475569', fontSize: '13px' }}>{d.StartTime || d['Start Time'] || '—'}</td>
                                        <td style={{ padding: '14px 20px', color: '#475569', fontSize: '13px' }}>{d.Amount || d['Amount'] || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

